import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, Row, Col, Card, Badge, Button, 
  Nav, Tab, Spinner, Alert, ListGroup, ProgressBar,
  ButtonGroup, Modal, Form
} from 'react-bootstrap';
import { 
  FaArrowLeft, FaFileContract, FaUsers, FaClipboardCheck, 
  FaHistory, FaPlus, FaUpload, FaCheckCircle,
  FaExclamationTriangle, FaClock, FaEdit, FaMagic,
  FaPen, FaMapMarkerAlt, FaInfoCircle, FaSearch, 
  FaListOl, FaSave, FaPaperPlane, FaUser, FaSignature,
  FaSitemap, FaGripVertical, FaFileAlt, FaFileSignature,
  FaArrowRight, FaFilePdf, FaDownload, FaDollarSign, FaFileInvoiceDollar,
  FaKey
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentFieldMapper from './DocumentFieldMapper';
import AddFillableFieldsModal from './AddFillableFieldsModal';
import DocumentPrefillModal from './DocumentPrefillModal';
import PDFImageViewer from './PDFImageViewer';
import { PDFFormViewer } from './PDFFormViewer';
import PDFFillerEditor from './PDFFillerEditor';
import TransactionPartyAssignment from './TransactionPartyAssignment';
import { TransactionTemplateManager } from './TransactionTemplateManager';
import MakeOfferModal from './MakeOfferModal';
import TransactionDocumentChecklist from './TransactionDocumentChecklist';
import DocumentChecklistWithUploads from './DocumentChecklistWithUploads';
import TransactionOffersPanel from './TransactionOffersPanel'; // KOPS-711
import BrokerReviewStatus from './BrokerReviewStatus';
import CommissionDisbursementAuthorization from './CommissionDisbursementAuthorization';
import CommissionPane from '../broker-portal/CommissionPane';
import LandlordInvoiceGenerator from './LandlordInvoiceGenerator';
import EditTransactionModal from './EditTransactionModal';
import AIComplianceDisplay from './AIComplianceDisplay';
import TransactionClosingManager from '../broker-portal/TransactionClosingManager';
import { toast } from 'react-toastify';

interface TransactionDetailsProps {
  transactionId: string;
  onBack: () => void;
  portal?: 'agent' | 'broker';
}

interface Transaction {
  id: string;
  file_number?: string;
  type: string;
  client_type?: string;
  stage: string;
  client_name: string;
  client_email: string;
  client_id: string;
  lead_id?: string;
  property_id?: string;
  brokerage_id?: string;
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    price?: string | number;
  };
  estimated_closing_date?: string;
  compliance_status: string;
  compliance_score?: number;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  documents?: TransactionDocument[];
  parties?: Party[];
  offers?: any[];
  submissions?: Submission[];
}

interface TransactionDocument {
  id: string;
  name?: string;
  filename?: string;
  original_filename?: string;
  file_type?: string;
  type?: string;
  status?: string;
  document_role: string;
  added_at: string;
  s3_url?: string;
  description?: string;
  anvil_template_id?: string;
  is_anvil_template?: boolean;
  original_s3_url?: string;
  previewUrl?: string;
  view_url?: string;
  pdffiller_template_id?: string;
  pdffiller_edit_url?: string;
  pdffiller_fillable_id?: string;
  // Broker review fields
  broker_review_status?: string;
  broker_compliance_score?: number;
  broker_reviewed_at?: string;
  latest_version_type?: string;
  latest_version_number?: number;
}

interface Party {
  id: string;
  transaction_id?: string;
  party_type: string;
  name: string | null;
  email?: string | null;
  phone?: string | null;
  role_description?: string | null;
  is_primary?: boolean | null;
  added_at?: string;
  user_id?: string | null;
  contact_id?: string | null;
}

interface Submission {
  id: string;
  submission_type: string;
  status: string;
  submitted_at: string;
  reviewed_at?: string;
  review_comments?: string;
  compliance_score?: number;
}

const toTitleCase = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getPartyDisplayLabel = (party: Party) => {
  const role = (party.role_description || '').toLowerCase();
  switch (party.party_type) {
    case 'buyer':
      return role === 'buyer_secondary' ? 'Buyer (Co-buyer)' : 'Buyer';
    case 'seller':
      return role === 'seller_secondary' ? 'Seller (Co-seller)' : 'Seller';
    case 'agent':
      if (role === 'buyer_agent') return "Buyer's Agent";
      if (role === 'seller_agent') return "Seller's Agent";
      return 'Agent';
    case 'broker':
      if (role === 'buyer_broker') return 'Buyer Broker';
      if (role === 'seller_broker') return 'Seller Broker';
      return 'Broker';
    case 'attorney':
      return 'Attorney';
    default:
      if (role === 'title_officer') return 'Title Officer';
      if (role === 'escrow_officer') return 'Escrow Officer';
      if (role === 'lender') return 'Lender';
      return 'Other';
  }
};

const getPartyBadgeVariant = (partyType: string) => {
  switch (partyType) {
    case 'buyer':
      return 'success';
    case 'seller':
      return 'primary';
    case 'agent':
      return 'info';
    case 'broker':
      return 'warning';
    case 'attorney':
      return 'danger';
    default:
      return 'secondary';
  }
};

const sortPartiesForPrefill = (parties: Party[]) => {
  return [...parties].sort((a, b) => {
    const aPrimary = a.is_primary ? 1 : 0;
    const bPrimary = b.is_primary ? 1 : 0;
    if (aPrimary !== bPrimary) {
      return bPrimary - aPrimary;
    }
    const aDate = a.added_at ? new Date(a.added_at).getTime() : Infinity;
    const bDate = b.added_at ? new Date(b.added_at).getTime() : Infinity;
    return aDate - bDate;
  });
};

const assignPartyGroup = (
  parties: Party[],
  prefix: string,
  target: Record<string, string>,
  includeSummary = false
) => {
  const ordered = sortPartiesForPrefill(parties);
  ordered.forEach((party, index) => {
    const idx = index + 1;
    if (party.name) {
      target[`${prefix}${idx}_name`] = party.name;
      if (includeSummary && idx === 1) {
        target[`${prefix}_name`] = party.name;
      }
    }
    if (party.email) {
      target[`${prefix}${idx}_email`] = party.email;
      if (includeSummary && idx === 1) {
        target[`${prefix}_email`] = party.email;
      }
    }
    if (party.phone) {
      target[`${prefix}${idx}_phone`] = party.phone;
      if (includeSummary && idx === 1) {
        target[`${prefix}_phone`] = party.phone;
      }
    }
  });
};

const assignSingleParty = (party: Party | undefined, baseKey: string, target: Record<string, string>) => {
  if (!party) return;
  if (party.name) target[`${baseKey}_name`] = party.name;
  if (party.email) target[`${baseKey}_email`] = party.email;
  if (party.phone) target[`${baseKey}_phone`] = party.phone;
};

const buildPdfFillerPartyData = (parties: Party[] = []) => {
  const data: Record<string, string> = {};
  const lowerRole = (party: Party) => (party.role_description || '').toLowerCase();

  const buyers = parties.filter(p => p.party_type === 'buyer');
  assignPartyGroup(buyers, 'buyer', data, true);

  const sellers = parties.filter(p => p.party_type === 'seller');
  assignPartyGroup(sellers, 'seller', data, true);

  const buyerAgents = parties.filter(p => p.party_type === 'agent' && lowerRole(p) === 'buyer_agent');
  assignPartyGroup(buyerAgents, 'buyer_agent', data);

  const sellerAgents = parties.filter(p => p.party_type === 'agent' && lowerRole(p) === 'seller_agent');
  assignPartyGroup(sellerAgents, 'seller_agent', data);

  const buyerBrokers = parties.filter(p => p.party_type === 'broker' && lowerRole(p) === 'buyer_broker');
  assignPartyGroup(buyerBrokers, 'buyer_broker', data);

  const sellerBrokers = parties.filter(p => p.party_type === 'broker' && lowerRole(p) === 'seller_broker');
  assignPartyGroup(sellerBrokers, 'seller_broker', data);

  assignSingleParty(parties.find(p => lowerRole(p) === 'title_officer'), 'title_officer', data);
  assignSingleParty(parties.find(p => lowerRole(p) === 'escrow_officer'), 'escrow_officer', data);
  assignSingleParty(parties.find(p => lowerRole(p) === 'lender'), 'lender', data);

  const attorneys = parties.filter(p => p.party_type === 'attorney');
  assignPartyGroup(attorneys, 'attorney', data);

  return data;
};

const partyToSigner = (party: Party) => ({
  id: party.id,
  name: party.name || 'Unnamed Party',
  email: party.email || '',
  role: party.role_description
    ? toTitleCase(party.role_description)
    : getPartyDisplayLabel(party),
  is_primary: Boolean(party.is_primary),
  party_type: party.party_type
});

const TransactionDetails: React.FC<TransactionDetailsProps> = ({ transactionId, onBack, portal = 'agent' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract tab from URL path
  const getTabFromPath = () => {
    const pathParts = location.pathname.split('/');
    const transactionIndex = pathParts.indexOf('transactions');
    if (transactionIndex !== -1 && pathParts[transactionIndex + 1] === transactionId) {
      const validTabs = ['overview', 'documents', 'templates', 'compliance', 'activity', 'parties', 'commission', 'invoice'];
      const urlTab = pathParts[transactionIndex + 2];
      return validTabs.includes(urlTab) ? urlTab : 'overview';
    }
    return 'overview';
  };
  
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  
  // Update tab when URL changes (browser back/forward)
  useEffect(() => {
    const newTab = getTabFromPath();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);
  const [selectedDocument, setSelectedDocument] = useState<TransactionDocument | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [signatureMode, setSignatureMode] = useState(false);
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [signatureFields, setSignatureFields] = useState<{
    id: string;
    type: 'signature' | 'date' | 'initial' | 'text';
    party_id: string;
    party_name: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editingDocument, setEditingDocument] = useState<TransactionDocument | null>(null);
  const [editSession, setEditSession] = useState<{ id: string; prefilledData: any; results?: any[] } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedDocuments, setEditedDocuments] = useState<Set<string>>(new Set());
  const [showFieldOverlay, setShowFieldOverlay] = useState(false);
  const [fieldOverlayData, setFieldOverlayData] = useState<any[]>([]);
  const [isDraggingField, setIsDraggingField] = useState(false);
  const [draggedField, setDraggedField] = useState<any>(null);
  const [placedFields, setPlacedFields] = useState<any[]>([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureAssignments, setSignatureAssignments] = useState<Record<string, any>>({});
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [showPreMappingModal, setShowPreMappingModal] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<string[]>([]);
  const [pendingSigners, setPendingSigners] = useState<any[]>([]);

  const partyPrefillData = React.useMemo(
    () => buildPdfFillerPartyData(transaction?.parties || []),
    [transaction?.parties]
  );

  // Memoize the callback to prevent infinite loops
  const handlePartiesUpdated = useCallback((updatedParties: Party[]) => {
    // Simply update local state - the TransactionPartyAssignment component handles saving
    setTransaction(prev => prev ? { ...prev, parties: updatedParties } : prev);
  }, []);
  const [showSignatureMappingModal, setShowSignatureMappingModal] = useState(false);
  const [currentMappingDocIndex, setCurrentMappingDocIndex] = useState(0);
  const [documentFieldMappings, setDocumentFieldMappings] = useState<Record<string, any>>({});
  const [showFillableModal, setShowFillableModal] = useState(false);
  const [selectedDocForFillable, setSelectedDocForFillable] = useState<any>(null);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const [brokerReviewRefresh, setBrokerReviewRefresh] = useState(0);
  const [checklistRefresh, setChecklistRefresh] = useState(0);
  const [showPartyMapperModal, setShowPartyMapperModal] = useState(false);
  const [selectedDocForPartyMapping, setSelectedDocForPartyMapping] = useState<any>(null);
  const [showMakeOfferModal, setShowMakeOfferModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadDocumentRole, setUploadDocumentRole] = useState<'initiating' | 'required' | 'supporting' | 'supplemental'>('required');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [showPdffillerEditor, setShowPdffillerEditor] = useState(false);
  const [pdffillerDocument, setPdffillerDocument] = useState<TransactionDocument | null>(null);
  const [pdffillerLoadingId, setPdffillerLoadingId] = useState<string | null>(null);
  const [pdffillerSaving, setPdffillerSaving] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);

  // Check if transaction is locked (completed or closed)
  const isTransactionLocked = transaction?.stage === 'completed' || !!transaction?.closed_at;

  const normalizeMimeTypeValue = useCallback((value: string) => {
    const lower = value.toLowerCase();

    if (lower.includes('pdf')) return 'application/pdf';
    if (lower.includes('png')) return 'image/png';
    if (lower.includes('jpeg') || lower.includes('jpg')) return 'image/jpeg';
    if (lower.includes('gif')) return 'image/gif';
    if (lower.startsWith('image/')) return lower;

    return lower;
  }, []);

  const determineFileType = useCallback(
    (doc: Partial<TransactionDocument>) => {
      const candidates = [
        doc.file_type,
        (doc as any)?.fileType,
        (doc as any)?.content_type,
        (doc as any)?.document_mime_type,
        (doc as any)?.mime_type
      ].filter(Boolean) as string[];

      for (const candidate of candidates) {
        const normalized = normalizeMimeTypeValue(candidate);
        if (normalized) {
          return normalized;
        }
      }

      const filename = (doc.original_filename || doc.filename || (doc as any)?.name || '')
        .toString()
        .toLowerCase();

      if (filename.endsWith('.pdf')) return 'application/pdf';
      if (filename.endsWith('.png')) return 'image/png';
      if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) return 'image/jpeg';
      if (filename.endsWith('.gif')) return 'image/gif';

      return candidates[0] || '';
    },
    [normalizeMimeTypeValue]
  );

  const getDocumentViewUrl = useCallback(
    (rawUrl?: string | null) => {
      if (!rawUrl) {
        return '';
      }

      const trimmedUrl = rawUrl.trim();

      if (!trimmedUrl) {
        return '';
      }

      if (trimmedUrl.startsWith('data:')) {
        return trimmedUrl;
      }

      if (trimmedUrl.startsWith('/api/pdf-proxy') || trimmedUrl.includes('/pdf-proxy?url=')) {
        return trimmedUrl;
      }

      if (/^https?:\/\//i.test(trimmedUrl)) {
        return `/api/pdf-proxy?url=${encodeURIComponent(trimmedUrl)}`;
      }

      return `/api/pdf-proxy?url=${encodeURIComponent(trimmedUrl)}`;
    },
    []
  );

  const normalizeDocument = useCallback(
    (doc: any): TransactionDocument => {
      if (!doc) {
        return doc;
      }

      const rawUrl =
        doc.previewUrl ||
        doc.preview_url ||
        doc.prefilledUrl ||
        doc.prefilled_url ||
        doc.s3_url ||
        doc.url ||
        doc.file_path ||
        doc.original_s3_url ||
        '';

    const normalized: TransactionDocument = {
      ...doc,
      original_filename: doc.original_filename || doc.filename || doc.name,
      file_type: determineFileType(doc),
      s3_url: doc.s3_url || doc.url || doc.file_path || doc.original_s3_url || rawUrl,
      original_s3_url: doc.original_s3_url || doc.s3_url || doc.url || doc.file_path || rawUrl,
      previewUrl: doc.previewUrl || doc.preview_url || doc.prefilledUrl || doc.prefilled_url,
      view_url: getDocumentViewUrl(rawUrl),
      pdffiller_template_id: doc.pdffiller_template_id,
      pdffiller_edit_url: doc.pdffiller_edit_url,
      pdffiller_fillable_id: doc.pdffiller_fillable_id,
      // Preserve broker review fields
      broker_review_status: doc.broker_review_status,
      broker_compliance_score: doc.broker_compliance_score,
      broker_reviewed_at: doc.broker_reviewed_at,
      latest_version_type: doc.latest_version_type,
      latest_version_number: doc.latest_version_number
    };

      return normalized;
    },
    [determineFileType, getDocumentViewUrl]
  );

  useEffect(() => {
    fetchTransactionDetails();
  }, [transactionId]);

  const showFloatingAlert = (variant: 'success' | 'danger', message: string) => {
    const alertDiv = document.createElement('div');
    alertDiv.className = `alert alert-${variant} alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3`;
    alertDiv.style.zIndex = '9999';
    alertDiv.innerHTML = `
      ${variant === 'success' ? '<strong>Success!</strong> ' : ''}${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv.remove(), 5000);
  };

  const fetchFieldOverlayData = async (sessionId: string, documentId: string) => {
    try {
      const response = await axiosInstance.get(
        `/documents/preparation/field-overlay/${sessionId}/${documentId}`
      );
      
      if (response.data.success && response.data.field_overlay_data) {
        setFieldOverlayData(response.data.field_overlay_data);
        console.log('Field overlay data loaded:', response.data.field_overlay_data);
        return response.data.field_overlay_data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching field overlay:', error);
      return [];
    }
  };

  // Helper function to get field value based on mapping
  const getFieldValue = (field: any, data: any) => {
    if (!data) return '';
    
    switch (field.mapping || field.type) {
      case 'client_name':
      case 'buyer_name':
      case 'text':
        return data.client_name || data.name || '';
      case 'client_email':
      case 'buyer_email':
        return data.client_email || data.email || '';
      case 'property_address':
        return data.property_address || '';
      case 'purchase_price':
      case 'price':
        const price = data.purchase_price || data.price || '';
        return price ? `$${price}` : '';
      case 'date':
      case 'contract_date':
        return new Date().toLocaleDateString();
      case 'signature':
        return 'Signature Here';
      case 'initial':
        return data.client_name ? 
          data.client_name.split(' ').map((n: string) => n[0]).join('') : '';
      default:
        return data[field.mapping] || '';
    }
  };

  // Helper function to get field color based on type
  const getFieldColor = (fieldType: string) => {
    switch (fieldType) {
      case 'signature':
        return '#a855f7'; // Purple
      case 'date':
        return '#22c55e'; // Green
      case 'text':
        return '#3b82f6'; // Blue
      case 'initial':
        return '#fb923c'; // Orange
      case 'checkbox':
        return '#ef4444'; // Red
      default:
        return '#3b82f6'; // Default blue
    }
  };

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/transactions/${transactionId}`);
      console.log('Transaction details response:', response.data);
      
      // Debug log the documents from the response
      if (response.data.transaction?.documents) {
        console.log('📋 Documents from /transactions/:id endpoint:', {
          count: response.data.transaction.documents.length,
          firstDoc: response.data.transaction.documents[0],
          hasBrokerReview: response.data.transaction.documents.some((d: any) => d.broker_review_status)
        });
      }

      const transactionData = response.data.transaction;

      console.log('Transaction data:', transactionData);
      console.log('🎯 Commission override data:', {
        commission_override_rate: transactionData?.commission_override_rate,
        commission_override_misc_fees: transactionData?.commission_override_misc_fees
      });
      console.log('Client info:', {
        client_id: transactionData?.client_id,
        client_name: transactionData?.client_name,
        client_email: transactionData?.client_email,
        lead_id: transactionData?.lead_id
      });
      console.log('Parties:', transactionData?.parties);
      console.log('Broker Reviews:', transactionData?.broker_reviews);
      console.log('Broker Reviews Count:', transactionData?.broker_reviews?.length || 0);

      const normalizedDocuments = (transactionData?.documents || []).map((doc: any) => normalizeDocument(doc));
      
      // Debug log the documents
      console.log('🔍 Documents from API:', {
        rawCount: transactionData?.documents?.length || 0,
        normalizedCount: normalizedDocuments.length,
        firstDoc: normalizedDocuments[0],
        hasReviewStatus: normalizedDocuments.some((d: any) => d.broker_review_status)
      });

      const normalizedTransaction = {
        ...transactionData,
        documents: normalizedDocuments,
        broker_reviews: transactionData.broker_reviews || [] // Ensure broker_reviews are included
      } as Transaction;

      setTransaction(normalizedTransaction);

      setSelectedDocument((prev) => {
        if (!prev) {
          return prev;
        }

        const updatedDoc = normalizedDocuments.find((doc: any) => doc.id === prev.id);
        return updatedDoc || prev;
      });
    } catch (err: any) {
      console.error('Error fetching transaction details:', err);
      setError(err.response?.data?.error || 'Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  const resetUploadState = () => {
    setSelectedUploadFile(null);
    setUploadError(null);
    setUploadDocumentRole('required');
  };

  const handleUploadModalClose = () => {
    if (uploadingDocument) {
      return;
    }
    setShowUploadModal(false);
    resetUploadState();
  };

  const handleUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setUploadError(null);
    if (!file) {
      setSelectedUploadFile(null);
      return;
    }

    // No file size limit - allow any size document to be uploaded

    setSelectedUploadFile(file);
  };

  const handleDocumentUpload = async () => {
    if (!selectedUploadFile) {
      setUploadError('Please choose a file to upload.');
      return;
    }

    try {
      setUploadingDocument(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('file', selectedUploadFile);

      const documentType = uploadDocumentRole === 'initiating'
        ? 'initiating_document'
        : 'transaction_document';

      formData.append('document_type', documentType);
      formData.append('description', `Uploaded for transaction ${transactionId}`);
      formData.append('owner_type', 'user');

      // Include transaction context for proper S3 organization
      if (transaction?.brokerage_id || (transaction as any)?.brokerageId) {
        formData.append('brokerage_id', transaction?.brokerage_id || (transaction as any)?.brokerageId || '');
      }
      formData.append('transaction_id', transactionId);
      // Agent ID will be taken from authenticated user in backend

      // Use streamlined transaction document upload endpoint
      const uploadResponse = await axiosInstance.post('/transaction-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      if (!uploadResponse.data?.success || !uploadResponse.data?.document) {
        throw new Error(uploadResponse.data?.error || 'Upload failed');
      }

      const documentId = uploadResponse.data.document.id || uploadResponse.data.document.document_id;

      if (!documentId) {
        throw new Error('Document ID missing from upload response');
      }

      await axiosInstance.post(`/transactions/${transactionId}/documents`, {
        document_id: documentId,
        document_role: uploadDocumentRole
      });

      showFloatingAlert('success', `${selectedUploadFile.name} uploaded successfully.`);
      setShowUploadModal(false);
      resetUploadState();
      await fetchTransactionDetails();
      
      // Trigger broker review status refresh
      setBrokerReviewRefresh(prev => prev + 1);
    } catch (err: any) {
      console.error('Error uploading document:', err);
      const message = err.response?.data?.error || err.message || 'Failed to upload document. Please try again.';
      setUploadError(message);
      showFloatingAlert('danger', message);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle uploads from the document checklist
  // Derive agent role from transaction type and client type
  const getAgentRole = (transaction: Transaction): string => {
    const type = transaction.type?.toLowerCase();
    const clientType = transaction.client_type?.toLowerCase();
    
    console.log('🎭 Deriving agent role:', {
      transactionType: type,
      clientType: clientType,
      rawTransaction: { type: transaction.type, client_type: transaction.client_type }
    });
    
    // Purchase transactions
    if (type === 'purchase') {
      if (clientType === 'buyer') {
        return 'buyer_agent';
      } else if (clientType === 'seller') {
        return 'seller_agent'; // Rare case: representing seller in a purchase
      }
    }
    // Listing transactions
    else if (type === 'listing' || type === 'sale') {
      if (clientType === 'seller') {
        return 'listing_agent';
      } else if (clientType === 'buyer') {
        return 'buyer_agent'; // Rare case: representing buyer in a listing
      }
    }
    // Referral transactions
    else if (type === 'referral') {
      return 'referring_agent';
    }
    
    // Default based on client type
    return clientType === 'buyer' ? 'buyer_agent' : 'listing_agent';
  };

  // Debug log transaction documents
  React.useEffect(() => {
    if (transaction?.documents) {
      console.log('📄 Transaction documents in state:', {
        count: transaction.documents.length,
        firstDoc: transaction.documents[0],
        hasReviewStatus: transaction.documents.some((d: any) => d.broker_review_status)
      });
    }
  }, [transaction?.documents]);

  const handleChecklistDocumentUpload = async (document: any, file: File, existingDocumentId?: string) => {
    try {
      // No file size check - allow any size document to be uploaded

      // Upload the file with transaction context
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', document.type || 'transaction_document');
      formData.append('description', document.name || file.name);
      formData.append('checklist_name', document.name); // Standardized filename from checklist
      formData.append('owner_type', 'user');
      
      // Include transaction context for proper S3 organization
      if (transaction?.brokerage_id || (transaction as any)?.brokerageId) {
        formData.append('brokerage_id', transaction?.brokerage_id || (transaction as any)?.brokerageId || '');
      }
      formData.append('transaction_id', transactionId);
      
      // If replacing, include the existing document ID to create a new version
      if (existingDocumentId) {
        console.log('🔄 TransactionDetails - Adding replaces_document_id:', existingDocumentId);
        formData.append('replaces_document_id', existingDocumentId);
      } else {
        console.log('📤 TransactionDetails - New document upload (not a replacement)');
      }
      // Agent ID will be taken from authenticated user in backend
      
      // Use streamlined transaction document upload endpoint
      const uploadResponse = await axiosInstance.post('/transaction-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      if (!uploadResponse.data?.success || !uploadResponse.data?.document) {
        throw new Error(uploadResponse.data?.error || 'Upload failed');
      }
      
      const documentId = uploadResponse.data.document.id || uploadResponse.data.document.document_id;
      if (!documentId) {
        throw new Error('Document ID missing from upload response');
      }
      
      // Associate document with transaction (only if it's not a replacement)
      if (!existingDocumentId) {
        await axiosInstance.post(`/transactions/${transactionId}/documents`, {
          document_id: documentId,
          document_role: 'required' // Must be one of: initiating, required, supporting, supplemental
        });
      } else {
        console.log('🔄 Skipping transaction association for document replacement');
      }
      
      if (existingDocumentId) {
        const versionInfo = uploadResponse.data.document.version_number ? 
          ` (v${uploadResponse.data.document.version_number})` : '';
        showFloatingAlert('success', `${document.name} replaced successfully${versionInfo}.`);
      } else {
        showFloatingAlert('success', `${document.name} uploaded successfully.`);
      }
      
      // Refresh transaction details to update the checklist
      await fetchTransactionDetails();
      
      // Trigger broker review status refresh
      setBrokerReviewRefresh(prev => prev + 1);
    } catch (err: any) {
      console.error('Error uploading document from checklist:', err);
      const message = err.response?.data?.error || err.message || 'Failed to upload document. Please try again.';
      showFloatingAlert('danger', message);
    }
  };

  const handleOpenPdffillerEditor = useCallback(async (doc: TransactionDocument) => {
    try {
      setPdffillerLoadingId(doc.id);
      const normalized = normalizeDocument(doc);

      if (!normalized.s3_url && !normalized.view_url) {
        toast.error('Document is missing a valid download URL.');
        return;
      }

      if (!normalized.pdffiller_template_id || !normalized.pdffiller_edit_url) {
        try {
          const infoResponse = await axiosInstance.get(`/documents/${doc.id}/fillable-info`);
          if (infoResponse?.data?.is_fillable) {
            normalized.pdffiller_template_id = infoResponse.data.template_id;
            (normalized as any).pdffiller_fillable_id = infoResponse.data.fillable_form_id;
            normalized.pdffiller_edit_url = infoResponse.data.edit_url || normalized.pdffiller_edit_url;
          }
        } catch (error) {
          console.warn('Unable to fetch PDFFiller template info:', error);
        }
      }

      if (!normalized.pdffiller_template_id) {
        try {
          const response = await axiosInstance.post(`/documents/${doc.id}/make-fillable`);
          const { template_id, fillable_form_id, edit_url } = response.data;
          normalized.pdffiller_template_id = template_id;
          if (fillable_form_id) {
            (normalized as any).pdffiller_fillable_id = fillable_form_id;
          }
          normalized.pdffiller_edit_url = edit_url || normalized.pdffiller_edit_url;
          await fetchTransactionDetails();
        } catch (error: any) {
          const message = error?.response?.data?.error || 'Failed to convert document to fillable template.';
          toast.error(message);
          return;
        }
      }

      setPdffillerDocument(normalized);
      setShowPdffillerEditor(true);
    } finally {
      setPdffillerLoadingId(null);
    }
  }, [fetchTransactionDetails, normalizeDocument]);

  const handlePdffillerSave = useCallback(async (doc: TransactionDocument, payload: { filledPdfBlob: Blob }) => {
    try {
      setPdffillerSaving(true);
      const formData = new FormData();
      const fileName = doc.original_filename || doc.filename || 'document.pdf';
      formData.append('file', payload.filledPdfBlob, fileName);

      await axiosInstance.put(`/documents/${doc.id}/replace`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document updated successfully');
      setShowPdffillerEditor(false);
      setPdffillerDocument(null);
      await fetchTransactionDetails();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to save updated document.';
      toast.error(message);
    } finally {
      setPdffillerSaving(false);
    }
  }, [fetchTransactionDetails]);

  const updateStage = async (newStage: string) => {
    try {
      await axiosInstance.put(`/transactions/${transactionId}/stage`, { stage: newStage });
      await fetchTransactionDetails();
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const handleEditDocument = async (document: TransactionDocument) => {
    try {
      setEditLoading(true);
      setEditingDocument(document);
      
      console.log('🔥 Edit clicked for document:', document);
      
      // Create a preparation session for this existing document
      const sessionResponse = await axiosInstance.post('/documents/preparation/prepare-session', {
        transaction_id: transactionId,
        existing_document_ids: [document.id],
        client_id: transaction?.client_id
      });

      console.log('✅ Session response:', sessionResponse.data);

      if (sessionResponse.data.success) {
        const sessionId = sessionResponse.data.session.id;
        
        console.log('📝 Pre-filling document with data:', {
          client_name: transaction?.client_name,
          client_email: transaction?.client_email,
          lead_id: transaction?.lead_id,
          transaction_type: transaction?.type
        });

        // Pre-fill the document with transaction data
        const preFillResponse = await axiosInstance.post('/documents/preparation/prefill', {
          session_id: sessionId,
          client_id: transaction?.client_id,
          lead_id: transaction?.lead_id,
          data: {
            // Transaction data
            transaction_type: transaction?.type,
            transaction_stage: transaction?.stage,
            
            // Client data - multiple variations for different form fields
            client_name: transaction?.client_name,
            buyer_name: transaction?.client_name,
            client_email: transaction?.client_email,
            buyer_email: transaction?.client_email,
            lead_id: transaction?.lead_id,
            
            // Additional name variations that might match form fields
            name: transaction?.client_name,
            full_name: transaction?.client_name,
            buyer_full_name: transaction?.client_name,
            purchaser_name: transaction?.client_name,
            
            // Property data (if available)
            property_address: transaction?.property?.address || '',
            purchase_price: transaction?.property?.price || '',
            
            // Agent data
            agent_name: 'Current Agent', // You might want to get this from user context
            
            // Date fields
            contract_date: new Date().toISOString().split('T')[0],
            closing_date: transaction?.estimated_closing_date || ''
          }
        });

        console.log('✅ Pre-fill response:', preFillResponse.data);
        console.log('📄 Pre-filled results:', preFillResponse.data.results);
        
        // Extract field overlay data if available
        if (preFillResponse.data.results && preFillResponse.data.results.length > 0) {
          const result = preFillResponse.data.results[0];
          if (result.field_overlay_data) {
            setFieldOverlayData(result.field_overlay_data);
            console.log('🎯 Field overlay data:', result.field_overlay_data);
            
            // Save field mappings to database when PDFFiller template is applied
            try {
              console.log('💾 Saving PDFFiller field mappings to database...');
              const saveResponse = await axiosInstance.post('/templates/save-field-mapping', {
                document_id: document.id,
                document_type: document.type || 'document',
                document_name: document.filename || document.original_filename,
                field_mappings: result.field_overlay_data.map((field: any) => ({
                  id: field.id || `field_${Date.now()}_${Math.random()}`,
                  type: field.type || 'text',
                  label: field.label || field.name || 'Field',
                  x: parseFloat(field.x) || 0,
                  y: parseFloat(field.y) || 0,
                  width: parseFloat(field.width) || 15,
                  height: parseFloat(field.height) || 5,
                  page: parseInt(field.page) || 1,
                  mapping: field.mapping || null,
                  required: field.required !== false
                }))
              });
              
              if (saveResponse.data.success) {
                console.log('✅ Field mappings saved to database');
              }
            } catch (saveError) {
              console.error('❌ Failed to save field mappings:', saveError);
            }
            
            // If there's an error but we have overlay data, show it anyway
            if (result.status === 'error' && result.field_overlay_data) {
              console.log('⚠️ PDF modification failed but overlay data is available');
              // Don't show edit modal, just show the overlay
              setShowFieldOverlay(true);
              setShowEditModal(false);
              return;
            }
          }
        }
        
        setEditSession({
          id: sessionId,
          prefilledData: preFillResponse.data,
          results: preFillResponse.data.results
        });
        
        setShowEditModal(true);
      }
    } catch (error: any) {
      console.error('❌ Error preparing document for editing:', error);
      alert('Failed to prepare document for editing. Error: ' + (error.response?.data?.error || error.message || 'Unknown error'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveAssignment = () => {
    // Open the signature assignment modal
    setShowSignatureModal(true);
  };

  const saveSignatureFieldsForDocument = async (documentId: string, fields: any[]) => {
    try {
      // Save signature fields for a specific document
      const response = await axiosInstance.post(`/documents/${documentId}/signature-fields`, {
        fields: fields
      });
      
      if (response.data.success) {
        console.log('✅ Signature fields saved for document:', documentId);
      }
    } catch (error) {
      console.error('Error saving signature fields:', error);
    }
  };

  const loadSignatureFieldsForDocument = async (documentId: string) => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}/signature-fields`);
      
      if (response.data.success && response.data.fields) {
        setPlacedFields(response.data.fields);
        console.log('✅ Loaded signature fields for document:', documentId);
      }
    } catch (error) {
      console.error('Error loading signature fields:', error);
      setPlacedFields([]); // Reset if no saved fields
    }
  };

  const saveSignatureAssignments = async () => {
    try {
      // Save signature field assignments for each document
      const assignments = Object.entries(signatureAssignments).map(([docId, fields]) => ({
        documentId: docId,
        fields: fields
      }));

      // Save to backend
      const response = await axiosInstance.post(`/transactions/${transactionId}/signature-assignments`, {
        assignments
      });

      if (response.data.success) {
        alert('Signature assignments saved successfully!');
        setShowSignatureModal(false);
        
        // Move to next step or update UI
        fetchTransactionDetails();
      }
    } catch (error) {
      console.error('Error saving signature assignments:', error);
      alert('Failed to save signature assignments');
    }
  };

  const handleDocumentSelection = (documentId: string, selected: boolean) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(documentId);
      } else {
        newSet.delete(documentId);
      }
      return newSet;
    });
  };

  const handleSelectAllDocuments = (selected: boolean) => {
    if (selected) {
      const allDocIds = transaction?.documents?.map(doc => doc.id) || [];
      setSelectedDocuments(new Set(allDocIds));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const sendSelectedToDocumenso = async () => {
    if (selectedDocuments.size === 0) {
      alert('Please select at least one document to send');
      return;
    }

    const selectedDocsArray = Array.from(selectedDocuments);
    
    // Prepare signers
    const signers = transaction?.parties && transaction.parties.length > 0 
      ? transaction.parties
          .filter(p => p.email)
          .map(partyToSigner)
      : transaction?.client_name && transaction?.client_email
        ? [{
            id: transaction.client_id || 'client',
            name: transaction.client_name,
            email: transaction.client_email,
            role: 'Client',
            is_primary: true,
            party_type: 'client'
          }]
        : [];
    
    if (signers.length === 0) {
      alert('No signers with email addresses found. Please add parties with email addresses to this transaction.');
      return;
    }
    
    // Check for PDFFiller template fields on selected documents
    const tempFieldMappings: Record<string, any> = {};
    for (const docId of selectedDocsArray) {
      const doc = transaction?.documents?.find(d => d.id === docId);
      if (doc) {
        try {
          // Check if this document has template fields
          const response = await axiosInstance.get(`/documents/${docId}/signature-fields`);
          if (response.data.success && response.data.fields && response.data.fields.length > 0) {
            console.log(`📋 Found ${response.data.fields.length} template fields for ${doc.filename}`);
            tempFieldMappings[docId] = response.data.fields;
          }
        } catch (error) {
          console.log(`No template fields for ${doc.filename}`);
        }
      }
    }
    
    // If we found template fields, use them
    if (Object.keys(tempFieldMappings).length > 0) {
      setDocumentFieldMappings(tempFieldMappings);
      console.log('🎯 Using PDFFiller template fields for documents:', tempFieldMappings);
    }

    // Show pre-mapping modal instead of sending directly
    setPendingDocuments(selectedDocsArray);
    setPendingSigners(signers);
    setShowPreMappingModal(true);
  };

  const confirmAndSendToDocumenso = async () => {
    try {
      console.log('📤 Sending for signatures:', { documents: pendingDocuments, signers: pendingSigners, fieldMappings: documentFieldMappings });

      const response = await axiosInstance.post(`/transactions/${transactionId}/send-for-signatures`, {
        documents: pendingDocuments,
        signers: pendingSigners,
        fieldMappings: documentFieldMappings // Include the manual field mappings
      });

      if (response.data.success) {
        const { results, successful_documents, failed_documents } = response.data;
        
        if (failed_documents > 0) {
          const failedDocs = results.filter((r: any) => r.status === 'error');
          alert(`Partially successful: ${successful_documents} documents sent, ${failed_documents} failed.\n\nErrors:\n${failedDocs.map((r: any) => `- ${r.document_id}: ${r.error}`).join('\n')}`);
        } else {
          alert(`Success! ${successful_documents} document${successful_documents > 1 ? 's' : ''} sent for electronic signatures.`);
        }

        // Clear selection and refresh
        setSelectedDocuments(new Set());
        setShowPreMappingModal(false);
        await fetchTransactionDetails();
      }
    } catch (error) {
      console.error('Error sending documents for signatures:', error);
      alert('Failed to send documents for signatures. Please try again.');
    }
  };

  const getStageProgress = (stage: string): number => {
    const stages = ['pre_contract', 'showing', 'under_contract', 'closing', 'completed'];
    const index = stages.indexOf(stage);
    return index === -1 ? 0 : ((index + 1) / stages.length) * 100;
  };

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      pre_contract: 'info',
      showing: 'primary',
      under_contract: 'warning',
      closing: 'success',
      completed: 'secondary'
    };
    return colors[stage] || 'secondary';
  };

  const getDocumentStatusIcon = (status?: string, documentId?: string) => {
    // Check if document has been edited and is ready to send
    if (documentId && editedDocuments.has(documentId)) {
      return <FaMagic className="text-primary" />;
    }
    
    switch (status) {
      case 'signed':
        return <FaCheckCircle className="text-success" />;
      case 'sent':
        return <FaClock className="text-warning" />;
      case 'pending':
        return <FaExclamationTriangle className="text-muted" />;
      default:
        return <FaFileContract className="text-secondary" />;
    }
  };
  
  const getDocumentStatusText = (status?: string, documentId?: string) => {
    // Check if document has been edited and is ready to send
    if (documentId && editedDocuments.has(documentId)) {
      return 'Ready to send';
    }
    
    return status || 'Draft';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading transaction details...</p>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Transaction</Alert.Heading>
        <p>{error || 'Transaction not found'}</p>
        <Button variant="outline-danger" onClick={onBack}>
          <FaArrowLeft className="me-2" />
          Back to Transactions
        </Button>
      </Alert>
    );
  }

  return (
    <div className="transaction-details">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="link" onClick={onBack} className="p-0 mb-2">
            <FaArrowLeft className="me-2" />
            Back to Transactions
          </Button>
          <h4 className="mb-0">{transaction.client_name}</h4>
          <span className="text-muted d-block">
            {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} Transaction
          </span>
          {transaction.file_number && (
            <span className="text-primary fw-bold">
              File #: {transaction.file_number}
            </span>
          )}
        </div>
        <ButtonGroup>
          <Button 
            variant="outline-primary"
            onClick={() => setShowEditTransactionModal(true)}
            disabled={isTransactionLocked}
            title={isTransactionLocked ? "Transaction is completed and cannot be edited" : "Edit transaction details"}
          >
            <FaEdit className="me-2" />
            Edit
          </Button>
          <Button 
            variant="primary"
            title="Add new document"
          >
            <FaPlus className="me-2" />
            Add Document
          </Button>
        </ButtonGroup>
      </div>

      {/* Completed Transaction Alert */}
      {isTransactionLocked && (
        <Alert variant="info" className="mb-3">
          <FaCheckCircle className="me-2" />
          <strong>Transaction Completed:</strong> You can still add documents if needed. 
          Transaction details and parties cannot be modified. Contact your broker for any other changes.
        </Alert>
      )}

      {/* Progress Bar */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Transaction Progress</h6>
            <Badge bg={getStageColor(transaction.stage)}>
              {transaction.stage.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          <ProgressBar 
            now={getStageProgress(transaction.stage)} 
            variant={getStageColor(transaction.stage)}
            label={`${getStageProgress(transaction.stage)}%`}
            style={{ height: '25px' }}
          />
          <div className="d-flex justify-content-between mt-2 small text-muted">
            <span>Pre-Contract</span>
            <span>Showing</span>
            <span>Under Contract</span>
            <span>Closing</span>
            <span>Completed</span>
          </div>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Tab.Container 
        activeKey={activeTab} 
        onSelect={(key) => {
          const newTab = key || 'overview';
          setActiveTab(newTab);
          
          // Update URL to reflect the selected tab
          const basePath = `/${portal}-portal/transactions/${transactionId}`;
          const newPath = newTab === 'overview' ? basePath : `${basePath}/${newTab}`;
          navigate(newPath);
        }}
      >
        <Card>
          <Card.Header className="bg-white">
            <Nav variant="tabs" className="border-0">
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <FaFileContract className="me-2" />
                  Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">
                  <FaFileContract className="me-2" />
                  Documents
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="templates">
                  <FaFileSignature className="me-2" />
                  Templates
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="compliance">
                  <FaClipboardCheck className="me-2" />
                  Compliance
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="parties">
                  <FaUsers className="me-2" />
                  Parties ({transaction.parties?.length || 0})
                </Nav.Link>
              </Nav.Item>
              {/* Show Invoice tab for rental/lease transactions */}
              {(transaction.type === 'rental' || transaction.type === 'lease') && (
                <Nav.Item>
                  <Nav.Link eventKey="invoice">
                    <FaFileInvoiceDollar className="me-2" />
                    Invoice
                  </Nav.Link>
                </Nav.Item>
              )}
              {/* Always show Commission tab for all transaction types */}
              <Nav.Item>
                <Nav.Link eventKey="commission">
                  <FaDollarSign className="me-2" />
                  Commission
                </Nav.Link>
              </Nav.Item>
              {/* Closing tab - only for brokers */}
              {portal === 'broker' && (
                <Nav.Item>
                  <Nav.Link eventKey="closing">
                    <FaKey className="me-2" />
                    Closing
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </Card.Header>

          <Card.Body>
            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                <Row>
                  <Col md={6}>
                    <h6>Transaction Information</h6>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="px-0">
                        <strong>Type:</strong> {transaction.type}
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0">
                        <strong>Stage:</strong> {transaction.stage ? transaction.stage.replace('_', ' ') : 'Not Set'}
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0">
                        <strong>Created:</strong> {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0">
                        <strong>Last Updated:</strong> {transaction.updated_at ? new Date(transaction.updated_at).toLocaleDateString() : 'N/A'}
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                  <Col md={6}>
                    <h6>Compliance Status</h6>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="px-0">
                        <strong>Status:</strong>{' '}
                        <Badge bg={transaction.compliance_status === 'approved' ? 'success' : 'warning'}>
                          {transaction.compliance_status}
                        </Badge>
                      </ListGroup.Item>
                      {transaction.compliance_score && (
                        <ListGroup.Item className="px-0">
                          <strong>Score:</strong> {transaction.compliance_score}%
                        </ListGroup.Item>
                      )}
                      <ListGroup.Item className="px-0">
                        <strong>Documents:</strong> {transaction.documents?.length || 0}
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0">
                        <strong>Submissions:</strong> {transaction.submissions?.length || 0}
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                </Row>

                {/* Property Information */}
                {transaction.property && (
                  <>
                    <hr className="my-4" />
                    <Row>
                      <Col md={12}>
                        <h6>Property Information</h6>
                        <ListGroup variant="flush">
                          {transaction.property?.address && (
                            <ListGroup.Item className="px-0">
                              <strong>Address:</strong> {transaction.property.address}
                              {transaction.property.city && (
                                <>, {transaction.property.city}</>
                              )}
                              {transaction.property.state && (
                                <>, {transaction.property.state}</>
                              )}
                              {transaction.property.zip && (
                                <> {transaction.property.zip}</>
                              )}
                            </ListGroup.Item>
                          )}
                          {transaction.property?.price && (
                            <ListGroup.Item className="px-0">
                              <strong>Sale/List Price:</strong> ${Number(transaction.property.price).toLocaleString()}
                            </ListGroup.Item>
                          )}
                          {transaction.estimated_closing_date && (
                            <ListGroup.Item className="px-0">
                              <strong>Estimated Closing Date:</strong> {new Date(transaction.estimated_closing_date).toLocaleDateString()}
                            </ListGroup.Item>
                          )}
                        </ListGroup>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Document Checklist - Commented out as it's now in Documents tab */}
                {/* <hr className="my-4" />
                <h6 className="mb-3">Document Checklist</h6>
                <TransactionDocumentChecklist
                  transactionId={transactionId}
                  transactionType={transaction.type}
                  onDocumentAction={(docId, action) => {
                    if (action === 'upload' || action === 'add') {
                      setActiveTab('documents');
                    } else if (action === 'view' && docId) {
                      const doc = transaction.documents?.find(d => d.id === docId);
                      if (doc) {
                        const normalizedDoc = normalizeDocument(doc);
                        setSelectedDocument(normalizedDoc);
                        setShowDocumentModal(true);
                      } else {
                        console.warn('Document not found in transaction for viewing', docId);
                      }
                    } else if (action === 'download' && docId) {
                      const doc = transaction.documents?.find(d => d.id === docId);
                      const downloadSource = doc?.original_s3_url || doc?.s3_url || doc?.view_url;
                      if (downloadSource) {
                        window.open(downloadSource, '_blank');
                      }
                    }
                  }}
                /> */}

                {/* Stage Actions */}
                <hr className="my-4" />
                <h6>Quick Actions</h6>
                <ButtonGroup>
                  {transaction.stage === 'pre_contract' && (
                    <Button 
                      variant="outline-primary" 
                      onClick={() => updateStage('showing')}
                      disabled={isTransactionLocked}
                    >
                      Move to Showing
                    </Button>
                  )}
                  {transaction.stage === 'showing' && (
                    <Button 
                      variant="outline-primary" 
                      onClick={() => updateStage('under_contract')}
                      disabled={isTransactionLocked}
                    >
                      Move to Under Contract
                    </Button>
                  )}
                  {transaction.stage === 'under_contract' && (
                    <Button 
                      variant="outline-primary" 
                      onClick={() => updateStage('closing')}
                      disabled={isTransactionLocked}
                    >
                      Move to Closing
                    </Button>
                  )}
                  {transaction.stage === 'closing' && portal === 'broker' && (
                    <Button 
                      variant="outline-success" 
                      onClick={() => updateStage('completed')}
                      disabled={isTransactionLocked}
                    >
                      <FaCheckCircle className="me-2" />
                      Mark as Completed
                    </Button>
                  )}
                </ButtonGroup>

                {/* Make Offer Section - Show only for purchase transactions in showing stage */}
                {transaction.type === 'purchase' && transaction.stage === 'showing' && (
                  <Card className="mt-4 border-primary shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-2 text-primary d-flex align-items-center">
                            <FaFileContract className="me-2" />
                            Ready to Make an Offer?
                          </h5>
                          <p className="text-muted mb-0">
                            Our AI assistant will help you create a competitive offer in minutes!
                          </p>
                          <small className="text-success">
                            <FaMagic className="me-1" />
                            AI pre-fills property details, market analysis, and standard terms
                          </small>
                        </div>
                        <Button 
                          variant="primary" 
                          size="lg"
                          onClick={() => setShowMakeOfferModal(true)}
                          className="px-4 py-3"
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          <FaMagic className="me-2" style={{ fontSize: '20px' }} />
                          Make Offer with AI
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Review Offers Section - For listing transactions */}
                {transaction.type === 'listing' && (transaction.stage === 'active' || transaction.stage === 'showing') && (
                  <Card className="mt-4 border-success shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-2 text-success d-flex align-items-center">
                            <FaClipboardCheck className="me-2" />
                            Manage Incoming Offers
                          </h5>
                          <p className="text-muted mb-0">
                            {transaction.offers?.length || 0} offer(s) received • AI analysis available
                          </p>
                        </div>
                        <Button
                          variant="success"
                          size="lg"
                          onClick={() => setActiveTab('documents')}
                          className="px-4"
                        >
                          <FaClipboardCheck className="me-2" />
                          Review Offers
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Tab.Pane>

              {/* Documents Tab */}
              <Tab.Pane eventKey="documents">
                {/* Warning if parties not defined */}
                {(!transaction?.parties || transaction.parties.length === 0) && (
                  <Alert variant="warning" className="mb-3">
                    <FaExclamationTriangle className="me-2" />
                    <strong>Action Required:</strong> Please add transaction parties (buyer, seller) in the "Parties" tab before using templates.
                    <Button 
                      variant="warning" 
                      size="sm" 
                      className="ms-3"
                      onClick={() => setActiveTab('parties')}
                    >
                      Go to Parties Tab
                    </Button>
                  </Alert>
                )}
                
                {/* Document Checklist with Upload Placeholders */}
                {(() => {
                  console.log('🔍 [TransactionDetails] Passing to DocumentChecklistWithUploads:', {
                    transactionType: transaction.type,
                    transactionRole: getAgentRole(transaction),
                    transactionStage: transaction.stage,
                    fullTransaction: transaction
                  });
                  return null;
                })()}
                <DocumentChecklistWithUploads
                  transactionId={transactionId}
                  transactionType={transaction.type}
                  transactionRole={getAgentRole(transaction)}
                  transactionStage={transaction.stage}
                  existingDocuments={transaction.documents || []}
                  onDocumentUpload={handleChecklistDocumentUpload}
                  brokerageId={transaction?.brokerage_id || (transaction as any)?.brokerageId}
                  refreshTrigger={checklistRefresh}
                  onBulkUpload={() => {
                    resetUploadState();
                    setShowUploadModal(true);
                  }}
                />
                
                {/* KOPS-711: Offers — each offer is its own document section on the deal */}
                <TransactionOffersPanel
                  transactionId={transactionId}
                  brokerageId={transaction?.brokerage_id || (transaction as any)?.brokerageId}
                  transactionType={transaction.type}
                  clientName={transaction.client_name}
                  onOffersChanged={() => fetchTransactionDetails()}
                />

                {/* Broker Review Status Component - Shows documents submitted for review */}
                <BrokerReviewStatus
                  transactionId={transactionId}
                  refreshTrigger={brokerReviewRefresh}
                  portal={portal}
                  onDocumentReviewed={() => {
                    setBrokerReviewRefresh(prev => prev + 1);
                    toast.success('Document reviewed successfully!');
                  }}
                />
                
                {/* Existing Documents Section - Show only if there are uploaded documents */}
                {transaction.documents && transaction.documents.length > 0 && (
                  <>
                    <hr className="my-4" />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Uploaded Documents ({transaction.documents.length})</h6>
                      <div className="d-flex gap-2">
                    {selectedDocuments.size > 0 && (
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={sendSelectedToDocumenso}
                      >
                        <FaFileSignature className="me-2" />
                        Send for Signatures ({selectedDocuments.size})
                      </Button>
                    )}
                    {editedDocuments.size > 0 && (
                      <Button 
                        variant="success" 
                        size="sm"
                        onClick={async () => {
                          const readyDocs = transaction?.documents?.filter(doc => editedDocuments.has(doc.id)) || [];
                          console.log(`Sending ${readyDocs.length} documents for signatures`);
                          
                          // Show modal or confirmation
                          const message = `Send ${readyDocs.length} document${readyDocs.length > 1 ? 's' : ''} for signatures to ${transaction?.client_name}?`;
                          if (window.confirm(message)) {
                            try {
                              // Prepare signers
                              const signers = transaction?.parties && transaction.parties.length > 0 
                                ? transaction.parties
                                    .filter(p => p.email)
                                    .map(partyToSigner)
                                : transaction?.client_name && transaction?.client_email
                                  ? [{
                                      id: transaction.client_id || 'client',
                                      name: transaction.client_name,
                                      email: transaction.client_email,
                                      role: 'Client',
                                      is_primary: true,
                                      party_type: 'client'
                                    }]
                                  : [];
                              
                              // Get document paths
                              const documentPaths = readyDocs.map(doc => doc.s3_url || '').filter(path => path);
                              
                              // Create session for batch
                              const sessionResponse = await axiosInstance.post('/documents/preparation/prepare-session', {
                                transaction_id: transactionId,
                                existing_document_ids: readyDocs.map(doc => doc.id)
                              });
                              
                              // Send all documents
                              const sendResponse = await axiosInstance.post('/documents/preparation/send-for-signing', {
                                session_id: sessionResponse.data.session.id,
                                document_paths: documentPaths,
                                signers: signers,
                                client_id: transaction?.client_id,
                                document_type: 'multiple',
                                document_title: `${readyDocs.length} Transaction Documents`
                              });
                              
                              if (sendResponse.data.success) {
                                // Clear the edited documents
                                setEditedDocuments(new Set());
                                
                                // Show success
                                const alert = document.createElement('div');
                                alert.className = 'alert alert-success alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
                                alert.style.zIndex = '9999';
                                alert.innerHTML = `
                                  <strong>Success!</strong> ${readyDocs.length} document${readyDocs.length > 1 ? 's' : ''} sent for signatures.
                                  <br><small>Queue position: ${sendResponse.data.queue_position}</small>
                                  <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                                `;
                                document.body.appendChild(alert);
                                setTimeout(() => alert.remove(), 5000);
                                
                                // Refresh transaction
                                await fetchTransactionDetails();
                              }
                            } catch (error) {
                              console.error('Error batch sending documents:', error);
                              alert('Failed to send documents. Please try again.');
                            }
                          }
                        }}
                      >
                        <FaPaperPlane className="me-2" />
                        Send All ({editedDocuments.size})
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        resetUploadState();
                        setShowUploadModal(true);
                      }}
                      title="Upload a new document"
                    >
                      <FaUpload className="me-2" />
                      Upload Document
                    </Button>
                      </div>
                    </div>

                    
                    {/* Select All Header */}
                    <div className="d-flex align-items-center mb-3 p-2 bg-light rounded">
                      <Form.Check
                        type="checkbox"
                        id="select-all-documents"
                        checked={selectedDocuments.size === transaction.documents.length && transaction.documents.length > 0}
                        onChange={(e) => handleSelectAllDocuments(e.target.checked)}
                        className="me-3"
                      />
                      <label htmlFor="select-all-documents" className="mb-0 fw-semibold">
                        Select All Documents ({transaction.documents.length})
                      </label>
                      {selectedDocuments.size > 0 && (
                        <Badge bg="primary" className="ms-2">
                          {selectedDocuments.size} selected
                        </Badge>
                      )}
                    </div>

                    <ListGroup>
                      {transaction.documents.map(doc => (
                        <ListGroup.Item key={doc.id} className="d-flex align-items-center">
                          {/* Selection Checkbox */}
                          <Form.Check
                            type="checkbox"
                            id={`doc-${doc.id}`}
                            checked={selectedDocuments.has(doc.id)}
                            onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                            className="me-3"
                          />
                          
                          {/* Document Info */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              {getDocumentStatusIcon(doc.status, doc.id)}
                              <span className="ms-2">{doc.original_filename || doc.filename || doc.name || 'Untitled Document'}</span>
                              {editedDocuments.has(doc.id) && (
                                <Badge bg="primary" className="ms-2">Ready to send</Badge>
                              )}
                              {selectedDocuments.has(doc.id) && (
                                <Badge bg="success" className="ms-2">
                                  <FaCheckCircle size={12} className="me-1" />
                                  Selected for Signature
                                </Badge>
                              )}
                            </div>
                            <small className="text-muted">
                              {doc.document_role} • {getDocumentStatusText(doc.status, doc.id)} • Added {doc.added_at ? new Date(doc.added_at).toLocaleDateString() : 'Unknown Date'}
                            </small>
                          </div>

                          {/* Action Buttons */}
                          <ButtonGroup size="sm">
                            <Button 
                              variant="outline-primary"
                              onClick={async () => {
                                const documentToDisplay = normalizeDocument(doc);
                                setSelectedDocument(documentToDisplay);
                                setShowDocumentModal(true);
                              }}
                            >
                              View
                            </Button>
                            
                            <Button 
                              variant="outline-secondary"
                              onClick={() => handleEditDocument(doc)}
                              disabled={editLoading}
                              title="Edit document with AI pre-fill"
                            >
                              {editLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                <FaMagic className="me-1" />
                              )}
                              Edit
                            </Button>

                            <Button
                              variant="outline-info"
                              onClick={() => {
                                setSelectedDocForFillable(doc);
                                setShowFillableModal(true);
                              }}
                              title="Convert to fillable PDF"
                            >
                              <FaFilePdf className="me-1" />
                              Make Fillable
                            </Button>

                            <Button
                              variant="outline-success"
                              onClick={() => handleOpenPdffillerEditor(doc)}
                              disabled={pdffillerLoadingId === doc.id}
                              title="Open PDFFiller editor"
                            >
                              {pdffillerLoadingId === doc.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <FaEdit className="me-1" />
                              )}
                              Edit in PDFFiller
                            </Button>
                          </ButtonGroup>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                )}
              </Tab.Pane>

              {/* Templates Tab */}
              <Tab.Pane eventKey="templates">
                <TransactionTemplateManager 
                  transactionId={transactionId}
                  brokerageId={transaction?.brokerage_id || (transaction as any)?.brokerageId}
                  onDocumentsCopied={fetchTransactionDetails}
                  transactionData={{
                    ...partyPrefillData,
                    property_address: transaction?.property?.address || '',
                    property_city: transaction?.property?.city || '',
                    property_state: transaction?.property?.state || '',
                    property_zip: transaction?.property?.zip || '',
                    purchase_price: transaction?.property?.price?.toString() || '',
                    closing_date: transaction?.estimated_closing_date || '',
                    buyer_name: partyPrefillData.buyer_name || transaction?.client_name || '',
                    buyer_email: partyPrefillData.buyer_email || transaction?.client_email || '',
                    buyer_phone: partyPrefillData.buyer_phone || '',
                    seller_name: partyPrefillData.seller_name || '',
                    seller_email: partyPrefillData.seller_email || '',
                    seller_phone: partyPrefillData.seller_phone || '',
                    buyer_agent_name: partyPrefillData.buyer_agent1_name || '',
                    buyer_agent_email: partyPrefillData.buyer_agent1_email || '',
                    seller_agent_name: partyPrefillData.seller_agent1_name || '',
                    seller_agent_email: partyPrefillData.seller_agent1_email || ''
                  }}
                />
              </Tab.Pane>

              {/* Compliance Tab */}
              <Tab.Pane eventKey="compliance">
                {/* AI Compliance Analysis */}
                <AIComplianceDisplay 
                  transaction={transaction} 
                  onNavigateToDocuments={() => setActiveTab('documents')}
                />
                
                {/* Historical Compliance Submissions */}
                <hr className="my-4" />
                <h6>Historical Compliance Submissions</h6>
                {transaction.submissions && transaction.submissions.length > 0 ? (
                  <ListGroup>
                    {transaction.submissions.map(submission => (
                      <ListGroup.Item key={submission.id}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <Badge bg={submission.status === 'approved' ? 'success' : 'warning'}>
                              {submission.status}
                            </Badge>
                            <p className="mb-1 mt-2">
                              <strong>{submission.submission_type.replace('_', ' ')}</strong>
                            </p>
                            <small className="text-muted">
                              Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                            </small>
                            {submission.review_comments && (
                              <p className="mt-2 mb-0">
                                <small>{submission.review_comments}</small>
                              </p>
                            )}
                          </div>
                          {submission.compliance_score && (
                            <div className="text-end">
                              <strong>{submission.compliance_score}%</strong>
                              <div className="small text-muted">Score</div>
                            </div>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Alert variant="info">
                    No compliance submissions yet. Documents will be automatically submitted when signed.
                  </Alert>
                )}
              </Tab.Pane>

              {/* Parties Tab */}
              <Tab.Pane eventKey="parties">
                <TransactionPartyAssignment
                  transactionId={transactionId}
                  transactionType={transaction?.type}
                  clientType={transaction?.client_type}
                  transactionStage={transaction?.stage}
                  isReadOnly={isTransactionLocked}
                  existingParties={(transaction?.parties || []).map((party: any) => ({
                    ...party,
                    name: party.name || '',
                    email: party.email || '',
                    phone: party.phone || ''
                  }))}
                  onPartiesUpdated={handlePartiesUpdated}
                />
              </Tab.Pane>
              {/* Invoice Tab - Only for rental/lease transactions */}
              {(transaction.type === 'rental' || transaction.type === 'lease') && (
                <Tab.Pane eventKey="invoice">
                  <LandlordInvoiceGenerator
                    property={{
                      address: transaction.property?.address || 
                        `${transaction.property?.address || 'Property Address'}, ${transaction.property?.city || ''}, ${transaction.property?.state || ''} ${transaction.property?.zip || ''}`
                    }}
                    landlord={{
                      name: transaction.parties?.find(p => p.party_type === 'seller' || p.party_type === 'landlord')?.name,
                      email: transaction.parties?.find(p => p.party_type === 'seller' || p.party_type === 'landlord')?.email,
                      phone: transaction.parties?.find(p => p.party_type === 'seller' || p.party_type === 'landlord')?.phone
                    }}
                    transactionId={transactionId}
                    onSave={(invoice) => {
                      fetchTransactionDetails();
                    }}
                  />
                </Tab.Pane>
              )}
              {/* Commission Tab - Always show for all transaction types */}
              <Tab.Pane eventKey="commission">
                <CommissionPane
                  transaction={transaction}
                  onReload={fetchTransactionDetails}
                  portal="broker"
                />
              </Tab.Pane>
              
              {/* Closing Tab - Only for brokers */}
              {portal === 'broker' && (
                <Tab.Pane eventKey="closing">
                  <TransactionClosingManager
                    transaction={transaction}
                    onUpdate={fetchTransactionDetails}
                  />
                </Tab.Pane>
              )}
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>

      <Modal
        show={showUploadModal}
        onHide={handleUploadModalClose}
        centered
        backdrop={uploadingDocument ? 'static' : true}
        keyboard={!uploadingDocument}
      >
        <Modal.Header closeButton={!uploadingDocument}>
          <Modal.Title>Upload Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="transaction-upload-file">
            <Form.Label>Select document</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,.doc,.docx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf"
              onChange={handleUploadFileChange}
              disabled={uploadingDocument}
            />
            <Form.Text className="text-muted">
              Accepted formats: PDF, DOC, DOCX, TXT, RTF (max 50MB).
            </Form.Text>
          </Form.Group>

          <Form.Group controlId="transaction-upload-role" className="mt-3">
            <Form.Label>Document role</Form.Label>
            <Form.Select
              value={uploadDocumentRole}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                setUploadDocumentRole(event.target.value as typeof uploadDocumentRole)
              }
              disabled={uploadingDocument}
            >
              <option value="required">Required</option>
              <option value="supporting">Supporting</option>
              <option value="supplemental">Supplemental</option>
              <option value="initiating">Initiating</option>
            </Form.Select>
          </Form.Group>

          {selectedUploadFile && (
            <div className="mt-3 small text-muted">
              Selected: <strong>{selectedUploadFile.name}</strong> ({(selectedUploadFile.size / (1024 * 1024)).toFixed(2)} MB)
            </div>
          )}

          {uploadError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {uploadError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleUploadModalClose} disabled={uploadingDocument}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDocumentUpload}
            disabled={!selectedUploadFile || uploadingDocument}
          >
            {uploadingDocument ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload Document'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Document Field Mapping Modal */}
      <Modal
        show={showDocumentModal}
        onHide={() => {
          setShowDocumentModal(false);
          setSelectedDocument(null);
          setShowFieldOverlay(false);
          setFieldOverlayData([]);
        }}
        onShow={async () => {
          if (!selectedDocument) {
            return;
          }

          try {
            const documentType = (selectedDocument as any).document_role ||
              (selectedDocument as any).document_type ||
              (selectedDocument as any).type ||
              'required';

            const response = await axiosInstance.get(`/templates/field-mapping/${documentType}`);

            if (response.data.success && response.data.fields.length > 0) {
              const overlayData = response.data.fields.map((field: any) => ({
                ...field,
                x: field.x || 20,
                y: field.y || 20,
                width: field.width || 20,
                height: field.height || 8,
                type: field.type || 'text',
                value: getFieldValue(field, {
                  client_name: transaction?.client_name,
                  client_email: transaction?.client_email,
                  property_address: transaction?.property?.address,
                  purchase_price: transaction?.property?.price
                }),
                style: {
                  position: 'absolute',
                  left: `${field.x || 20}%`,
                  top: `${field.y || 20}%`,
                  width: `${field.width || 20}%`,
                  height: `${field.height || 8}%`,
                  backgroundColor: getFieldColor(field.type || 'text') + '1A',
                  border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${getFieldColor(field.type || 'text')}`,
                  borderRadius: '4px',
                  padding: '8px'
                }
              }));

              setFieldOverlayData(overlayData);
            } else {
              setFieldOverlayData([]);
            }
          } catch (error) {
            console.error('❌ Error loading field mappings:', error);
            setFieldOverlayData([]);
          }
        }}
        size="xl"
        fullscreen
        className="field-mapping-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center justify-content-between w-100">
            <div>
              <FaFileContract className="me-2" />
              {selectedDocument?.original_filename || selectedDocument?.filename || 'Document Preview'}
              <Badge bg="secondary" className="ms-2">Ready for Signature Assignment</Badge>
              {fieldOverlayData.length > 0 && (
                <Badge bg="success" className="ms-2">
                  <FaCheckCircle className="me-1" />
                  Template Applied ({fieldOverlayData.length} fields)
                </Badge>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: 'calc(100vh - 120px)' }}>
          {showFieldOverlay ? (
            // Show the drag-and-drop field mapper
            <DocumentFieldMapper
              documentUrl={selectedDocument?.view_url || selectedDocument?.s3_url || '#'}
              documentName={selectedDocument?.original_filename || selectedDocument?.filename || 'Document'}
              documentId={selectedDocument?.id || ''}
              clientData={{
                client_name: transaction?.client_name,
                client_email: transaction?.client_email,
                property_address: transaction?.property?.address,
                purchase_price: transaction?.property?.price
              }}
              detectedFields={fieldOverlayData}
              onSaveMapping={async (fields: any[]) => {
                console.log('DocumentFieldMapper - detectedFields:', fieldOverlayData);
                console.log('DocumentFieldMapper - saving fields:', fields);
                // Save the field mapping
                try {
                  // Use consistent document type determination
                  const documentType = (selectedDocument as any)?.document_role || 
                                     (selectedDocument as any)?.document_type || 
                                     (selectedDocument as any)?.type || 
                                     'required';
                  
                  const response = await axiosInstance.post('/templates/save-field-mapping', {
                    document_type: documentType,
                    document_name: selectedDocument?.original_filename || selectedDocument?.filename,
                    field_mappings: fields,
                    document_id: selectedDocument?.id
                  });
                  
                  if (response.data.success) {
                    console.log('Field mapping saved successfully');
                    // Update the field overlay data for immediate use
                    const overlayData = fields.map((field: any) => ({
                      ...field,
                      value: getFieldValue(field, {
                        client_name: transaction?.client_name,
                        client_email: transaction?.client_email
                      }),
                      style: {
                        position: 'absolute',
                        left: `${field.x}%`,
                        top: `${field.y}%`,
                        width: `${field.width}%`,
                        height: `${field.height}%`,
                        backgroundColor: getFieldColor(field.type) + '1A',
                        border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${getFieldColor(field.type)}`,
                        borderRadius: '4px',
                        padding: '8px'
                      }
                    }));
                    setFieldOverlayData(overlayData);
                  }
                } catch (error) {
                  console.error('Error saving field mapping:', error);
                }
              }}
              existingFields={fieldOverlayData.map(f => ({
                id: f.id || `field_${Date.now()}`,
                type: f.type || 'text',
                label: f.label || f.name || 'Field',
                x: parseFloat(f.style?.left || '0'),
                y: parseFloat(f.style?.top || '0'),
                width: parseFloat(f.style?.width || '20'),
                height: parseFloat(f.style?.height || '5'),
                page: f.page || 1,
                mapping: f.mapping
              }))}
            />
          ) : (
            // Show the regular document viewer
            <div className="d-flex h-100">
              {/* Document Viewer - Left Side */}
              <div 
                className="flex-grow-1" 
                style={{ borderRight: '1px solid #dee2e6', position: 'relative' }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.backgroundColor = isDraggingField ? 'rgba(59, 130, 246, 0.05)' : '';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.backgroundColor = '';
                  
                  if (!draggedField) return;
                  
                  // Calculate position relative to the container
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  
                  // Add the field to placed fields
                  const newField = {
                    id: `field_${Date.now()}`,
                    ...draggedField,
                    x: Math.max(0, Math.min(85, x)),
                    y: Math.max(0, Math.min(90, y)),
                    width: draggedField.type === 'signature' ? 25 : 20,
                    height: draggedField.type === 'signature' ? 8 : 3,
                    style: {
                      position: 'absolute',
                      left: `${Math.max(0, Math.min(85, x))}%`,
                      top: `${Math.max(0, Math.min(90, y))}%`,
                      width: draggedField.type === 'signature' ? '25%' : '20%',
                      height: draggedField.type === 'signature' ? '8%' : '3%',
                      backgroundColor: getFieldColor(draggedField.type) + '1A',
                      border: `2px ${draggedField.type === 'signature' ? 'dashed' : 'solid'} ${getFieldColor(draggedField.type)}`,
                      borderRadius: '4px',
                      padding: '8px',
                      cursor: 'move',
                      zIndex: 20
                    }
                  };
                  
                  const updatedFields = [...placedFields, newField];
                  setPlacedFields(updatedFields);
                  setIsDraggingField(false);
                  setDraggedField(null);
                  
                  // Auto-save signature fields to backend
                  if (selectedDocument?.id) {
                    saveSignatureFieldsForDocument(selectedDocument.id, updatedFields);
                  }
                }}
              >
              {/* PDF Container with overlays */}
              {selectedDocument?.file_type?.includes('pdf') ? (
                <div style={{ 
                  position: 'relative', 
                  width: '100%', 
                  height: '100%',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}>
                  {showFieldOverlay || signatureMode ? (
                    <PDFImageViewer
                      documentUrl={selectedDocument?.view_url || selectedDocument?.s3_url || ''}
                      documentId={selectedDocument?.id || ''}
                    >
                    {fieldOverlayData.length > 0 && fieldOverlayData.map((field: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          position: 'absolute',
                          left: field.x ? `${field.x}%` : field.style?.left,
                          top: field.y ? `${field.y}%` : field.style?.top,
                          width: field.width ? `${field.width}%` : field.style?.width,
                          height: field.height ? `${field.height}%` : field.style?.height,
                          backgroundColor: field.color || getFieldColor(field.type || 'text') + '1A',
                          border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${getFieldColor(field.type || 'text')}`,
                          borderRadius: '4px',
                          padding: '8px',
                          pointerEvents: 'none'
                        }}
                      >
                        <div style={{
                          color: field.color || getFieldColor(field.type || 'text'),
                          fontWeight: 'bold',
                          fontSize: field.type === 'signature' ? '12px' : '14px'
                        }}>
                          {field.type === 'signature' && <FaSignature size={18} className="me-1" />}
                          {field.value || field.label || field.name}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280' }}>
                          {field.label || field.name || 'Field'}
                        </div>
                      </div>
                    ))}
                    </PDFImageViewer>
                  ) : (
                    <PDFFormViewer
                      documentUrl={selectedDocument?.view_url || selectedDocument?.s3_url || selectedDocument?.original_s3_url || ''}
                      documentId={selectedDocument?.id || ''}
                      documentName={selectedDocument?.filename || selectedDocument?.original_filename || 'Document'}
                      allowEdit={true}
                      onSave={async () => {
                        await fetchTransactionDetails();
                      }}
                    />
                  )}

                  {showFieldOverlay && (
                    <div style={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    zIndex: 30
                  }}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        const downloadSource = selectedDocument?.original_s3_url || selectedDocument?.s3_url;
                        if (downloadSource) {
                          const link = document.createElement('a');
                          link.href = downloadSource;
                          link.download = selectedDocument.original_filename || selectedDocument.filename || 'document.pdf';
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }
                      }}
                    >
                      <FaDownload className="me-2" />
                      Download PDF
                    </Button>
                  </div>
                  )}

                  {fieldOverlayData.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(34, 197, 94, 0.95)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      zIndex: 30,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <FaCheckCircle size={20} />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>Template Applied Successfully</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>
                          {fieldOverlayData.length} field{fieldOverlayData.length > 1 ? 's' : ''} ready for Documenso
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedDocument?.file_type?.includes('image') ? (
                <div style={{ 
                  position: 'relative',
                  height: '100%', 
                  overflow: 'auto'
                }}>
                  <img
                    src={selectedDocument?.view_url || selectedDocument?.s3_url || '#'}
                    alt="Document Preview"
                    style={{ width: '100%', height: 'auto' }}
                  />
                  
                  {/* Field overlays for images can be positioned properly */}
                  {fieldOverlayData.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none'
                    }}>
                      {fieldOverlayData.map((field, index) => (
                        <div
                          key={index}
                          style={{
                            ...field.style,
                            pointerEvents: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: field.type === 'signature' ? 'center' : 'flex-start'
                          }}
                        >
                          <div style={{
                            color: field.color || getFieldColor(field.type),
                            fontWeight: 'bold',
                            fontSize: field.type === 'signature' ? '12px' : '14px',
                            textAlign: field.type === 'signature' ? 'center' : 'left'
                          }}>
                            {field.type === 'signature' && <FaSignature size={20} className="mb-1" />}
                            <div>{field.value}</div>
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#6b7280',
                            marginTop: '2px'
                          }}>
                            {field.label || field.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center py-5">
                    <FaFileContract size={60} className="text-muted mb-3" />
                    <p>Preview not available for this file type</p>
                    <p className="text-muted">File type: {selectedDocument?.file_type || 'Unknown'}</p>
                    {selectedDocument?.s3_url && (
                      <Button
                        variant="primary"
                        href={selectedDocument.view_url || selectedDocument.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in New Tab
                      </Button>
                    )}
                  </div>
                </div>
              )}
                
                {/* Fallback overlay for when no field data is available */}
                {showFieldOverlay && fieldOverlayData.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    zIndex: 10
                  }}>
                    <Spinner animation="border" variant="light" className="mb-2" />
                    <div>Scanning document for fields...</div>
                    <small>This may take a few seconds on first scan</small>
                  </div>
                )}
            </div>

            {/* Signature Assignment Panel - Right Side */}
            <div style={{ width: '400px', padding: '1.5rem', backgroundColor: '#f8f9fa', height: '100%', overflowY: 'auto' }}>
              <h5 className="mb-4">
                <FaPen className="me-2 text-primary" />
                Signature Assignment
              </h5>

              {/* Document Info */}
              <Card className="mb-4">
                <Card.Body className="p-3">
                  <h6 className="card-title mb-2">Document Details</h6>
                  <div className="small text-muted">
                    <div className="mb-1">
                      <strong>Name:</strong> {selectedDocument?.original_filename || selectedDocument?.filename}
                    </div>
                    <div className="mb-1">
                      <strong>Type:</strong> {selectedDocument?.file_type}
                    </div>
                    <div>
                      <strong>Status:</strong> {getDocumentStatusIcon(selectedDocument?.status)} {selectedDocument?.status || 'Draft'}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Transaction Parties */}
              <Card className="mb-4">
                <Card.Body className="p-3">
                  <h6 className="card-title mb-3">Transaction Parties</h6>
                  {transaction?.parties && transaction.parties.length > 0 ? (
                    <div className="space-y-2">
                      {transaction.parties.map((party, index) => (
                        <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-white rounded border">
                          <div>
                            <div className="fw-semibold">{party.name}</div>
                            <div className="small text-muted">{party.email || 'No email provided'}</div>
                            <div className="small">
                              <Badge bg={getPartyBadgeVariant(party.party_type)} className="me-1">
                                {getPartyDisplayLabel(party)}
                              </Badge>
                              {party.role_description && toTitleCase(party.role_description) !== getPartyDisplayLabel(party) && (
                                <Badge bg="light" text="dark" className="me-1">
                                  {toTitleCase(party.role_description)}
                                </Badge>
                              )}
                              {party.is_primary && (
                                <Badge bg="primary">Primary</Badge>
                              )}
                            </div>
                          </div>
                          <Form.Check 
                            type="checkbox"
                            id={`party-${index}`}
                            label="Include"
                            defaultChecked={true}
                          />
                        </div>
                      ))}
                    </div>
                  ) : transaction?.client_name ? (
                    <div>
                      <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded border mb-2">
                        <div>
                          <FaUser className="me-2 text-primary" />
                          <span className="fw-semibold">{transaction.client_name}</span>
                          <Badge bg="primary" className="ms-2">Primary Client</Badge>
                          {transaction.lead_id && (
                            <small className="text-muted ms-2">(Lead #{transaction.lead_id.slice(-5)})</small>
                          )}
                          {transaction.client_email && (
                            <div className="small text-muted mt-1">{transaction.client_email}</div>
                          )}
                        </div>
                        <Form.Check 
                          type="checkbox" 
                          id="primary-client"
                          label="Include"
                          defaultChecked={true}
                        />
                      </div>
                      <Alert variant="info" className="mb-0">
                        <small>
                          <FaInfoCircle className="me-1" />
                          Client automatically included from transaction. You can add other parties in the Parties tab.
                        </small>
                      </Alert>
                    </div>
                  ) : (
                    <Alert variant="warning" className="mb-0">
                      <FaExclamationTriangle className="me-2" />
                      No parties found. Add parties to enable signature assignment.
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Signature Fields */}
              <Card className="mb-4">
                <Card.Body className="p-3">
                  <h6 className="card-title mb-3">
                    <FaSignature className="me-2" />
                    Signature Field Mapping
                  </h6>
                  
                  {/* Client Mapping Display */}
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center mb-2">
                      <FaUser className="text-primary me-2" />
                      <strong>Primary Signer</strong>
                    </div>
                    <div className="ms-4">
                      <div className="d-flex align-items-center mb-1">
                        <FaCheckCircle className="text-success me-2" style={{ fontSize: '0.875rem' }} />
                        <span><strong>{transaction?.client_name || 'Brian Johnson'}</strong></span>
                      </div>
                      <div className="small text-muted ms-4">
                        {transaction?.client_email || 'brian.johnson@email.com'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <Form.Label className="small fw-semibold">Auto-Fill Field Detection:</Form.Label>
                    <div className="mt-2">
                      <div 
                        className="d-flex align-items-center mb-2 p-2 rounded"
                        draggable
                        onDragStart={(e) => {
                          setIsDraggingField(true);
                          setDraggedField({
                            type: 'text',
                            label: 'Client Name',
                            value: transaction?.client_name || 'Brian Johnson'
                          });
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onDragEnd={() => {
                          setIsDraggingField(false);
                          setDraggedField(null);
                        }}
                        style={{ 
                          cursor: 'grab',
                          backgroundColor: isDraggingField && draggedField?.type === 'text' ? '#e9ecef' : 'transparent',
                          border: '1px solid transparent',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.border = '1px solid #dee2e6';
                        }}
                        onMouseLeave={(e) => {
                          if (!isDraggingField || draggedField?.type !== 'text') {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.border = '1px solid transparent';
                          }
                        }}
                      >
                        <FaGripVertical className="text-muted me-2" />
                        <Form.Check 
                          type="checkbox"
                          id="name-mapping"
                          defaultChecked={true}
                          className="me-2"
                          readOnly
                        />
                        <div className="flex-grow-1">
                          <span className="d-block">Client Name Fields</span>
                          <small className="text-muted">Buyer Name, Client Name, Full Name → <strong>{transaction?.client_name || 'Brian Johnson'}</strong></small>
                        </div>
                      </div>
                      
                      <div 
                        className="d-flex align-items-center mb-2 p-2 rounded"
                        draggable
                        onDragStart={(e) => {
                          setIsDraggingField(true);
                          setDraggedField({
                            type: 'signature',
                            label: 'Signature Field',
                            value: 'Signature Here'
                          });
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        onDragEnd={() => {
                          setIsDraggingField(false);
                          setDraggedField(null);
                        }}
                        style={{ 
                          cursor: 'grab',
                          backgroundColor: isDraggingField && draggedField?.type === 'signature' ? '#e9ecef' : 'transparent',
                          border: '1px solid transparent',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.border = '1px solid #dee2e6';
                        }}
                        onMouseLeave={(e) => {
                          if (!isDraggingField || draggedField?.type !== 'signature') {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.border = '1px solid transparent';
                          }
                        }}
                      >
                        <FaGripVertical className="text-muted me-2" />
                        <Form.Check 
                          type="checkbox"
                          id="signature-field"
                          defaultChecked={true}
                          className="me-2"
                          readOnly
                        />
                        <div className="flex-grow-1">
                          <span className="d-block">Signature Field</span>
                          <small className="text-muted">Auto-detected on document</small>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check 
                          type="checkbox"
                          id="date-field"
                          defaultChecked={true}
                          className="me-2"
                          readOnly
                        />
                        <div className="flex-grow-1">
                          <span className="d-block">Date Field</span>
                          <small className="text-muted">Today's date will be inserted</small>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check 
                          type="checkbox"
                          id="print-name-field"
                          defaultChecked={true}
                          className="me-2"
                          readOnly
                        />
                        <div className="flex-grow-1">
                          <span className="d-block">Print Name Field</span>
                          <small className="text-muted">{transaction?.client_name || 'Brian Johnson'}</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {fieldOverlayData.length > 0 ? (
                    <Alert variant="success" className="small mb-3">
                      <FaCheckCircle className="me-1" />
                      <strong>{fieldOverlayData.length} fields mapped!</strong> Field positions are saved and will be applied to the document when sending to Documenso.
                    </Alert>
                  ) : (
                    <Alert variant="info" className="small mb-3">
                      <FaInfoCircle className="me-1" />
                      AI will automatically detect and map all signature fields when you send for signature
                    </Alert>
                  )}

                  <div className="d-grid gap-2">
                    {fieldOverlayData.length > 0 && (
                      <Alert variant="info" className="small mb-2">
                        <FaCheckCircle className="me-1" />
                        <strong>Template Ready!</strong> {fieldOverlayData.length} field{fieldOverlayData.length > 1 ? 's' : ''} are mapped and will be applied when sending to Documenso.
                        <br />
                        <small className="text-muted">Click below to view/edit field positions on the document.</small>
                      </Alert>
                    )}
                    <Button 
                      variant={fieldOverlayData.length > 0 ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={async () => {
                        setShowFieldOverlay(true);
                        
                        // Load saved signature fields for this document
                        if (selectedDocument?.id) {
                          await loadSignatureFieldsForDocument(selectedDocument.id);
                        }
                        
                        // First, try to get AI-detected fields
                        if (selectedDocument && editSession?.id) {
                          try {
                            const detectedFields = await fetchFieldOverlayData(
                              editSession.id,
                              selectedDocument.id
                            );
                            if (detectedFields && detectedFields.length > 0) {
                              setFieldOverlayData(detectedFields);
                              console.log('Using AI-detected fields:', detectedFields);
                              return; // Use detected fields instead of saved template
                            }
                          } catch (error) {
                            console.error('Error fetching detected fields:', error);
                          }
                        }
                        
                        // If no detected fields, fetch existing field mappings for this document type
                        if (selectedDocument) {
                          try {
                            const documentType = selectedDocument.document_role || 'generic';
                            const response = await axiosInstance.get(`/templates/field-mapping/${documentType}`);
                            
                            if (response.data.success && response.data.fields.length > 0) {
                              console.log(`Found ${response.data.fields.length} saved field mappings for ${documentType}`);
                              
                              // Convert saved fields to overlay data format
                              const overlayData = response.data.fields.map((field: any) => ({
                                ...field,
                                value: getFieldValue(field, {
                                  client_name: transaction?.client_name,
                                  client_email: transaction?.client_email,
                                  property_address: transaction?.property?.address,
                                  purchase_price: transaction?.property?.price
                                }),
                                style: {
                                  position: 'absolute',
                                  left: `${field.x}%`,
                                  top: `${field.y}%`,
                                  width: `${field.width}%`,
                                  height: `${field.height}%`,
                                  backgroundColor: getFieldColor(field.type) + '1A',
                                  border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${getFieldColor(field.type)}`,
                                  borderRadius: '4px',
                                  padding: '8px'
                                }
                              }));
                              
                              setFieldOverlayData(overlayData);
                            }
                          } catch (error) {
                            console.error('Error fetching field mappings:', error);
                            // Continue with empty field mappings
                          }
                        }
                      }}
                    >
                      <FaSitemap className="me-1" />
                      {fieldOverlayData.length > 0 ? 'View/Edit Field Mapping' : 'Map Fields (Drag & Drop)'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Signing Order */}
              <Card className="mb-4">
                <Card.Body className="p-3">
                  <h6 className="card-title mb-3">
                    <FaListOl className="me-2" />
                    Signing Order
                  </h6>
                  
                  <Form.Select className="mb-3" size="sm">
                    <option value="parallel">All parties sign simultaneously</option>
                    <option value="sequential">Sequential signing order</option>
                    <option value="custom">Custom workflow</option>
                  </Form.Select>

                  <div className="small text-muted">
                    <FaClock className="me-1" />
                    Estimated completion: 2-3 business days
                  </div>
                </Card.Body>
              </Card>

            </div>
          </div>
        )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDocumentModal(false);
                setSelectedDocument(null);
              }}
            >
              Close
            </Button>
          </div>
          <div>
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={handleSaveAssignment}
            >
              <FaSave className="me-1" />
              Save Assignment
            </Button>
            <Button
              variant="warning"
              className="me-2"
              disabled={!transaction?.documents || transaction?.documents?.length === 0}
              onClick={() => setShowPrefillModal(true)}
            >
              <FaMagic className="me-1" />
              Pre-fill Documents
            </Button>
            <Button
              variant="success"
              className="me-2"
              disabled={!transaction?.documents || transaction?.documents?.length === 0}
              onClick={async () => {
                // Check if all documents have signature fields mapped
                const unmappedDocs = [];
                for (const doc of transaction.documents || []) {
                  try {
                    const response = await axiosInstance.get(`/documents/${doc.id}/signature-fields`);
                    if (!response.data.fields || response.data.fields.length === 0) {
                      unmappedDocs.push(doc.name);
                    }
                  } catch {
                    unmappedDocs.push(doc.name);
                  }
                }
                
                if (unmappedDocs.length > 0) {
                  alert(`Please map signature fields for the following documents:\n${unmappedDocs.join('\n')}`);
                  return;
                }
                
                // Send all documents to DocuSign/Documenso
                try {
                  // Collect field mappings for the current document if in view modal
                  const fieldMappings: Record<string, any> = {};
                  
                  if (selectedDocument && fieldOverlayData.length > 0) {
                    // We're in the document view modal and have field mappings
                    fieldMappings[selectedDocument.id] = fieldOverlayData.map((field: any) => ({
                      type: field.type || 'signature',
                      x: field.x,
                      y: field.y,
                      width: field.width || 15,
                      height: field.height || 5,
                      page: field.page || 1,
                      name: field.name || field.label || 'Signature',
                      label: field.label || field.name || 'Signature',
                      required: true
                    }));
                    console.log('📋 Including field mappings for document:', selectedDocument.filename, fieldMappings[selectedDocument.id]);
                  }
                  
                  const response = await axiosInstance.post(`/transactions/${transactionId}/send-for-signatures`, {
                    documents: transaction.documents?.map(doc => doc.id) || [],
                    signers: transaction.parties && transaction.parties.length > 0
                      ? transaction.parties.filter(p => p.email).map(partyToSigner)
                      : transaction?.client_name && transaction?.client_email
                        ? [{
                            id: transaction.client_id || 'client',
                            name: transaction.client_name,
                            email: transaction.client_email,
                            role: 'Client',
                            is_primary: true,
                            party_type: 'client'
                          }]
                        : [],
                    fieldMappings: fieldMappings // Include field mappings
                  });
                  
                  if (response.data.success) {
                    alert('Documents sent for signature successfully!');
                    fetchTransactionDetails();
                  }
                } catch (error) {
                  console.error('Error sending documents:', error);
                  alert('Failed to send documents for signature');
                }
              }}
            >
              <FaPaperPlane className="me-1" />
              Send All to Documenso
            </Button>
            <Button
              variant="primary"
              disabled={!transaction?.parties?.length && !transaction?.client_name}
              onClick={async () => {
                try {
                  // If no parties but has client, use client as primary signer
                  const signers = transaction?.parties && transaction.parties.length > 0 
                    ? transaction.parties
                        .filter(p => p.email)
                        .map(partyToSigner)
                    : transaction?.client_name && transaction?.client_email
                      ? [{
                          id: transaction.client_id || 'client',
                          name: transaction.client_name,
                          email: transaction.client_email,
                          role: 'Client',
                          is_primary: true,
                          party_type: 'client'
                        }]
                      : [];
                  
                  if (signers.length === 0) {
                    alert('No signers available');
                    return;
                  }
                  
                  // Prepare session data
                  const sessionData = {
                    session_id: editSession?.id || `temp-${Date.now()}`,
                    document_paths: [selectedDocument?.s3_url || ''],
                    signers: signers,
                    client_id: transaction?.client_id,
                    document_type: selectedDocument?.document_role || 'contract',
                    document_title: selectedDocument?.original_filename || selectedDocument?.filename || 'Document'
                  };
                  
                  console.log('Sending document for signature:', sessionData);
                  
                  // Call the send-for-signing endpoint
                  const response = await axiosInstance.post('/documents/preparation/send-for-signing', sessionData);
                  
                  if (response.data.success) {
                    // Close modal and show success
                    setShowDocumentModal(false);
                    setSelectedDocument(null);
                    
                    // Show success message
                    const alert = document.createElement('div');
                    alert.className = 'alert alert-success alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
                    alert.style.zIndex = '9999';
                    alert.innerHTML = `
                      <strong>Success!</strong> Document sent for signatures to ${signers.length} recipient(s).
                      <br><small>Queue position: ${response.data.queue_position}</small>
                      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                    `;
                    document.body.appendChild(alert);
                    setTimeout(() => alert.remove(), 5000);
                    
                    // Refresh transaction to update document status
                    await fetchTransactionDetails();
                  }
                } catch (error) {
                  console.error('Error sending for signature:', error);
                  alert('Failed to send document for signature. Please try again.');
                }
              }}
            >
              <FaPaperPlane className="me-1" />
              Send for Signature
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Document Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingDocument(null);
          setEditSession(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaMagic className="me-2" />
            Edit Document - {editingDocument?.original_filename || editingDocument?.filename || 'Document'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editSession?.prefilledData ? (
            <div>
              <Alert variant="info" className="mb-3">
                <strong>AI Pre-filled Information</strong>
                <p className="mb-0">This document has been pre-filled with transaction data. Review the field mappings below.</p>
              </Alert>
              
              {/* Show Pre-fill Results */}
              {editSession?.results && editSession.results.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">Pre-fill Results</h6>
                  {editSession.results.map((result: any, idx: number) => (
                    <Card key={idx} className="mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold">Document {idx + 1}</span>
                          <Badge bg={result.status === 'prepared' ? 'success' : 'danger'}>
                            {result.status}
                          </Badge>
                        </div>
                        
                        {result.status === 'prepared' && (
                          <>
                            <div className="mb-2">
                              <small className="text-muted">Fields Filled:</small> 
                              <strong className="ms-2">{result.fields_filled || 0}</strong>
                            </div>
                            
                            {result.mappings && result.mappings.length > 0 && (
                              <div>
                                <small className="text-muted d-block mb-2">Field Mappings:</small>
                                <ListGroup variant="flush">
                                  {result.mappings.map((mapping: any, midx: number) => (
                                    <ListGroup.Item key={midx} className="px-0 py-2">
                                      <div className="d-flex justify-content-between">
                                        <small className="text-primary">{mapping.fieldName}</small>
                                        <small className="text-success">{mapping.value}</small>
                                      </div>
                                    </ListGroup.Item>
                                  ))}
                                </ListGroup>
                              </div>
                            )}
                            
                            {result.method && (
                              <div className="mt-2">
                                <small className="text-muted">Method:</small>
                                <Badge bg="secondary" className="ms-2">{result.method}</Badge>
                              </div>
                            )}
                            
                            {(result.s3_path || result.local_path) && (
                              <div className="mt-3">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    // TODO: Implement document preview
                                    console.log('View pre-filled document:', result);
                                  }}
                                >
                                  <FaSearch className="me-1" />
                                  View Pre-filled Document
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                        
                        {result.error && (
                          <Alert variant="danger" className="mb-0 mt-2">
                            <small>{result.error}</small>
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Original Form - Now shows below results */}
              <Form>
                <Row>
                  <Col md={6}>
                    <h6 className="border-bottom pb-2 mb-3">Review Client Information</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Client Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        defaultValue={transaction?.client_name || ''}
                        placeholder="Enter client name"
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Client Email</Form.Label>
                      <Form.Control 
                        type="email" 
                        defaultValue={transaction?.client_email || ''}
                        placeholder="Enter client email"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <h6 className="border-bottom pb-2 mb-3">Transaction Details</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction Type</Form.Label>
                      <Form.Select defaultValue={transaction?.type || ''}>
                        <option value="">Select type</option>
                        <option value="purchase">Purchase</option>
                        <option value="sale">Sale</option>
                        <option value="lease">Lease</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Stage</Form.Label>
                      <Form.Control 
                        type="text" 
                        defaultValue={transaction?.stage?.replace('_', ' ') || ''}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <h6 className="border-bottom pb-2 mb-3">Property Information</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Property Address</Form.Label>
                      <Form.Control 
                        type="text" 
                        defaultValue={transaction?.property?.address || ''}
                        placeholder="Enter property address"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Price</Form.Label>
                      <Form.Control 
                        type="number" 
                        defaultValue={transaction?.property?.price || ''}
                        placeholder="Enter purchase price"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <h6 className="border-bottom pb-2 mb-3">Important Dates</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Contract Date</Form.Label>
                      <Form.Control 
                        type="date" 
                        defaultValue={new Date().toISOString().split('T')[0]}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Estimated Closing Date</Form.Label>
                      <Form.Control 
                        type="date" 
                        defaultValue={transaction?.estimated_closing_date || ''}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="border-top pt-3 mt-3">
                  <h6 className="mb-3">Document Status</h6>
                  <div className="d-flex align-items-center">
                    {getDocumentStatusIcon(editingDocument?.status)}
                    <span className="ms-2">
                      Current Status: <Badge bg="secondary">{editingDocument?.status || 'Draft'}</Badge>
                    </span>
                  </div>
                </div>
              </Form>
            </div>
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading document data...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setEditingDocument(null);
              setEditSession(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            disabled={!editSession?.prefilledData}
            onClick={async () => {
              if (editingDocument) {
                // Mark document as edited
                setEditedDocuments(prev => new Set(prev).add(editingDocument.id));
                
                // Close the modal
                setShowEditModal(false);
                setEditingDocument(null);
                setEditSession(null);
                
                // Show success message
                const alert = document.createElement('div');
                alert.className = 'alert alert-success alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
                alert.style.zIndex = '9999';
                alert.innerHTML = `
                  <strong>Document saved!</strong> Ready to send for signatures.
                  <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                `;
                document.body.appendChild(alert);
                setTimeout(() => alert.remove(), 3000);
              }
            }}
          >
            Save Draft
          </Button>
          <Button
            variant="success"
            onClick={() => {
              // Send for signatures logic
              console.log('Sending for signatures...');
            }}
          >
            <FaPaperPlane className="me-2" />
            Send for Signatures
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Signature Assignment Modal */}
      <Modal
        show={showSignatureModal}
        onHide={() => setShowSignatureModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileSignature className="me-2" />
            Configure Signature Fields
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FaInfoCircle className="me-2" />
            Configure signature fields for each document. These will be used when sending documents through Documenso.
          </Alert>
          
          {transaction?.documents && transaction.documents.length > 0 ? (
            <div className="signature-assignments">
              {transaction.documents.map((doc: any) => (
                <Card key={doc.id} className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FaFileAlt className="me-2" />
                      {doc.name}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={12}>
                        <h6>Signature Fields</h6>
                        <div className="signature-fields mb-3">
                          {signatureAssignments[doc.id]?.signatures?.map((sig: any, index: number) => (
                            <div key={index} className="d-flex align-items-center mb-2">
                              <Form.Select
                                size="sm"
                                className="me-2"
                                value={sig.assignedTo || ''}
                                onChange={(e) => {
                                  const newAssignments = { ...signatureAssignments };
                                  if (!newAssignments[doc.id]) {
                                    newAssignments[doc.id] = { signatures: [], initials: [], dates: [] };
                                  }
                                  newAssignments[doc.id].signatures[index].assignedTo = e.target.value;
                                  setSignatureAssignments(newAssignments);
                                }}
                              >
                                <option value="">Select Signer</option>
                                {transaction?.parties?.map((party: any) => {
                                  const roleLabel = party.role_description
                                    ? toTitleCase(party.role_description)
                                    : getPartyDisplayLabel(party);
                                  return (
                                    <option key={party.id} value={party.id}>
                                      {party.name} ({roleLabel})
                                    </option>
                                  );
                                })}
                              </Form.Select>
                              <span className="text-muted">
                                Page {sig.page}, Position: ({sig.x}, {sig.y})
                              </span>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              const newAssignments = { ...signatureAssignments };
                              if (!newAssignments[doc.id]) {
                                newAssignments[doc.id] = { signatures: [], initials: [], dates: [] };
                              }
                              newAssignments[doc.id].signatures.push({
                                page: 1,
                                x: 100,
                                y: 100,
                                assignedTo: ''
                              });
                              setSignatureAssignments(newAssignments);
                            }}
                          >
                            <FaPlus className="me-1" />
                            Add Signature Field
                          </Button>
                        </div>

                        <h6>Initial Fields</h6>
                        <div className="initial-fields mb-3">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              const newAssignments = { ...signatureAssignments };
                              if (!newAssignments[doc.id]) {
                                newAssignments[doc.id] = { signatures: [], initials: [], dates: [] };
                              }
                              newAssignments[doc.id].initials.push({
                                page: 1,
                                x: 200,
                                y: 100,
                                assignedTo: ''
                              });
                              setSignatureAssignments(newAssignments);
                            }}
                          >
                            <FaPlus className="me-1" />
                            Add Initial Field
                          </Button>
                        </div>

                        <h6>Date Fields</h6>
                        <div className="date-fields">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              const newAssignments = { ...signatureAssignments };
                              if (!newAssignments[doc.id]) {
                                newAssignments[doc.id] = { signatures: [], initials: [], dates: [] };
                              }
                              newAssignments[doc.id].dates.push({
                                page: 1,
                                x: 300,
                                y: 100,
                                assignedTo: ''
                              });
                              setSignatureAssignments(newAssignments);
                            }}
                          >
                            <FaPlus className="me-1" />
                            Add Date Field
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <Alert variant="warning">
              No documents available. Please add documents to the transaction first.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignatureModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={saveSignatureAssignments}
            disabled={!transaction?.documents || transaction?.documents?.length === 0}
          >
            <FaSave className="me-2" />
            Save Signature Assignments
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pre-Mapping Confirmation Modal */}
      <Modal
        show={showPreMappingModal}
        onHide={() => setShowPreMappingModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileSignature className="me-2" />
            Review Documents Before Sending for Signatures
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FaInfoCircle className="me-2" />
            <strong>Field Mapping Confirmation</strong>
            <br />
            Please review the documents and signers below. Field mappings will be automatically detected and applied when sending for signatures.
          </Alert>

          {/* Selected Documents Review */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">
                <FaFileContract className="me-2" />
                Selected Documents ({pendingDocuments.length})
              </h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {pendingDocuments.map(docId => {
                  const document = transaction?.documents?.find(doc => doc.id === docId);
                  return (
                    <ListGroup.Item key={docId} className="px-0">
                      <div className="d-flex align-items-center">
                        <FaFileAlt className="text-primary me-2" />
                        <div>
                          <strong>{document?.original_filename || document?.filename || 'Document'}</strong>
                          <div className="small text-muted">
                            {document?.document_role} • Added {document?.added_at ? new Date(document.added_at).toLocaleDateString() : 'Unknown Date'}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>

          {/* Signers Review */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">
                <FaUsers className="me-2" />
                Signers ({pendingSigners.length})
              </h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {pendingSigners.map((signer, index) => (
                  <ListGroup.Item key={index} className="px-0">
                    <div className="d-flex align-items-center">
                      <FaUser className="text-success me-2" />
                      <div>
                        <strong>{signer.name}</strong>
                        <div className="small text-muted">{signer.email} • Role: {signer.role}</div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>

          {/* Field Mapping Info */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">
                <FaSitemap className="me-2" />
                Automatic Field Detection
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Alert variant="success" className="mb-2">
                  <FaCheckCircle className="me-1" />
                  <strong>Smart Field Detection Enabled</strong>
                </Alert>
                <div className="small text-muted">
                  The system will automatically:
                  <ul className="mb-0 mt-2">
                    <li>Detect signature fields in each document</li>
                    <li>Map signer information to appropriate fields</li>
                    <li>Apply saved templates if available</li>
                    <li>Generate signature requests for each signer</li>
                  </ul>
                </div>
              </div>

              <div className="bg-light p-3 rounded">
                <strong>Preview Mappings:</strong>
                <div className="mt-2">
                  {pendingSigners.map((signer, index) => (
                    <div key={index} className="small mb-1">
                      <FaArrowRight className="text-muted me-1" />
                      <strong>{signer.name}</strong> will sign as <Badge bg="secondary">{signer.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPreMappingModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              // Close pre-mapping modal and open signature mapping interface
              setShowPreMappingModal(false);
              setShowSignatureMappingModal(true);
            }}
          >
            <FaEdit className="me-1" />
            Map Signature Fields
          </Button>
          <Button
            variant="success"
            onClick={confirmAndSendToDocumenso}
          >
            <FaPaperPlane className="me-1" />
            Confirm & Send for Signatures
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Multi-Document Signature Mapping Modal */}
      <Modal
        show={showSignatureMappingModal}
        onHide={() => setShowSignatureMappingModal(false)}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaSitemap className="me-2" />
            Map Signature Fields - Document {currentMappingDocIndex + 1} of {pendingDocuments.length}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh', overflow: 'hidden' }}>
          {pendingDocuments.length > 0 && (
            <>
              {/* Document Navigation */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0">
                    {transaction?.documents?.find(doc => doc.id === pendingDocuments[currentMappingDocIndex])?.original_filename || 'Document'}
                  </h6>
                  <small className="text-muted">
                    Map signature fields for all signers on this document
                  </small>
                </div>
                <ButtonGroup>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentMappingDocIndex === 0}
                    onClick={() => setCurrentMappingDocIndex(prev => prev - 1)}
                  >
                    <FaArrowLeft className="me-1" /> Previous
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentMappingDocIndex === pendingDocuments.length - 1}
                    onClick={() => setCurrentMappingDocIndex(prev => prev + 1)}
                  >
                    Next <FaArrowRight className="ms-1" />
                  </Button>
                </ButtonGroup>
              </div>

              {/* Progress Indicator */}
              <ProgressBar className="mb-3" style={{ height: '10px' }}>
                {pendingDocuments.map((_, index) => (
                  <ProgressBar
                    key={index}
                    variant={documentFieldMappings[pendingDocuments[index]] ? 'success' : 'secondary'}
                    now={100 / pendingDocuments.length}
                    onClick={() => setCurrentMappingDocIndex(index)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </ProgressBar>

              {/* Field Mapper Component */}
              <div style={{ height: 'calc(100% - 120px)' }}>
                <DocumentFieldMapper
                  documentId={pendingDocuments[currentMappingDocIndex]}
                  documentUrl={
                    transaction?.documents?.find(doc => doc.id === pendingDocuments[currentMappingDocIndex])?.view_url ||
                    transaction?.documents?.find(doc => doc.id === pendingDocuments[currentMappingDocIndex])?.s3_url ||
                    ''
                  }
                  documentName={transaction?.documents?.find(doc => doc.id === pendingDocuments[currentMappingDocIndex])?.original_filename || ''}
                  onFieldsUpdate={(fields: any[]) => {
                    console.log(`📍 Updating fields for document ${currentMappingDocIndex + 1}:`, fields.length, 'fields');
                    setDocumentFieldMappings(prev => {
                      const updated = {
                        ...prev,
                        [pendingDocuments[currentMappingDocIndex]]: fields
                      };
                      console.log(`📊 Total mapped documents: ${Object.keys(updated).length} of ${pendingDocuments.length}`);
                      return updated;
                    });
                  }}
                  initialFields={documentFieldMappings[pendingDocuments[currentMappingDocIndex]]}
                  signers={pendingSigners}
                  mode="signature-only"
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                {Object.keys(documentFieldMappings).length} of {pendingDocuments.length} documents mapped
                {Object.keys(documentFieldMappings).length < pendingDocuments.length && (
                  <span className="text-warning ms-2">
                    (Map all documents to enable sending)
                  </span>
                )}
              </small>
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSignatureMappingModal(false);
                  setDocumentFieldMappings({});
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="ms-2"
                onClick={() => {
                  // Save all mappings and go back to pre-mapping modal
                  setShowSignatureMappingModal(false);
                  setShowPreMappingModal(true);
                }}
                disabled={Object.keys(documentFieldMappings).length === 0}
              >
                <FaSave className="me-1" />
                Save Mappings ({Object.keys(documentFieldMappings).length})
              </Button>
              <Button
                variant="success"
                className="ms-2"
                onClick={async () => {
                  // Apply mappings and send to Documenso
                  console.log('📍 Applying field mappings:', documentFieldMappings);
                  setShowSignatureMappingModal(false);
                  await confirmAndSendToDocumenso();
                }}
                disabled={Object.keys(documentFieldMappings).length < pendingDocuments.length}
              >
                <FaPaperPlane className="me-1" />
                Apply & Send ({Object.keys(documentFieldMappings).length}/{pendingDocuments.length})
              </Button>
            </div>
          </div>
      </Modal.Footer>
    </Modal>

      {/* PDFFiller Editor Modal */}
      {pdffillerDocument && (
        <PDFFillerEditor
          show={showPdffillerEditor}
          onHide={() => {
            if (pdffillerSaving) {
              return;
            }
            setShowPdffillerEditor(false);
            setPdffillerDocument(null);
          }}
          documentUrl={pdffillerDocument.view_url || pdffillerDocument.s3_url || ''}
          documentName={pdffillerDocument.original_filename || pdffillerDocument.filename || 'Document'}
          transactionData={transaction}
          onSave={(data) => handlePdffillerSave(pdffillerDocument!, data)}
        />
      )}

      {/* Add Fillable Fields Modal */}
      {selectedDocForFillable && (
        <AddFillableFieldsModal
          show={showFillableModal}
          onHide={() => {
            setShowFillableModal(false);
            setSelectedDocForFillable(null);
          }}
          documentUrl={selectedDocForFillable?.url || `${process.env.REACT_APP_API_URL}/documents/${selectedDocForFillable?.id}/download`}
          documentName={selectedDocForFillable?.original_filename || selectedDocForFillable?.filename || selectedDocForFillable?.name || 'document.pdf'}
          transactionData={transaction}
          onFieldsAdded={() => {
            alert('Document converted to fillable form! It\'s now available in the Document Center as a template.');
            fetchTransactionDetails(); // Refresh to show any updates
          }}
        />
      )}

      {/* Document Pre-fill Modal */}
      <DocumentPrefillModal
        show={showPrefillModal}
        onHide={() => setShowPrefillModal(false)}
        transactionId={transactionId}
        documents={transaction?.documents || []}
        onDocumentsPrefilled={(prefilledDocs) => {
          console.log('Documents pre-filled:', prefilledDocs);
          // Refresh transaction to show pre-filled status
          fetchTransactionDetails();
        }}
      />

      {/* Make Offer Modal */}
      {transaction && (
        <MakeOfferModal
          show={showMakeOfferModal}
          onHide={() => setShowMakeOfferModal(false)}
          transaction={transaction}
          onOfferCreated={(offerId) => {
            console.log('Offer created:', offerId);
            setShowMakeOfferModal(false);
            // Refresh transaction to show new offer
            fetchTransactionDetails();
            // Navigate to documents tab to show the generated offer
            setActiveTab('documents');
          }}
        />
      )}
      
      {/* Edit Transaction Modal */}
      {transaction && (
        <EditTransactionModal
          show={showEditTransactionModal}
          onHide={() => setShowEditTransactionModal(false)}
          transaction={transaction}
          onUpdate={async () => {
            await fetchTransactionDetails();
            setChecklistRefresh(prev => prev + 1);
          }}
        />
      )}
      
      {/* Enhanced Navigation Tab Styles for Better Accessibility */}
      <style>{`
        /* Make navigation tabs larger and more readable */
        .transaction-details .nav-tabs {
          border-bottom: 3px solid #dee2e6 !important;
        }
        
        .transaction-details .nav-tabs .nav-link {
          font-size: 1.125rem !important;  /* 18px instead of default 16px */
          font-weight: 500 !important;
          padding: 0.75rem 1.5rem !important;  /* More padding for easier clicking */
          color: #495057 !important;
          border: 2px solid transparent !important;
          border-bottom: none !important;
          transition: all 0.3s ease !important;
          min-width: 120px;  /* Ensure consistent tab width */
          text-align: center;
        }
        
        .transaction-details .nav-tabs .nav-link:hover {
          background-color: #f8f9fa !important;
          color: #007bff !important;
          border-color: #dee2e6 !important;
        }
        
        .transaction-details .nav-tabs .nav-link.active {
          font-weight: 600 !important;  /* Bold for active tab */
          color: #007bff !important;
          background-color: #fff !important;
          border-color: #dee2e6 #dee2e6 #fff !important;
          border-width: 2px 2px 0 2px !important;
          border-bottom: 3px solid #fff !important;
          position: relative;
          top: 2px;
        }
        
        /* Make icons in tabs slightly larger */
        .transaction-details .nav-tabs .nav-link svg {
          font-size: 1.25rem !important;  /* Slightly larger icons */
        }
        
        /* Add visual separator between tabs */
        .transaction-details .nav-tabs .nav-item:not(:last-child) {
          margin-right: 0.25rem;
        }
        
        /* Add focus outline for keyboard navigation */
        .transaction-details .nav-tabs .nav-link:focus {
          outline: 2px solid #007bff !important;
          outline-offset: 2px !important;
        }
        
        /* Improve contrast for better readability */
        .transaction-details .nav-tabs .nav-link.active::after {
          content: '';
          position: absolute;
          top: -3px;
          left: 0;
          right: 0;
          height: 3px;
          background-color: #007bff;
        }
        
        /* Make the card header taller to accommodate larger tabs */
        .transaction-details .card-header {
          padding: 1rem 1.25rem !important;
        }
        
        /* Responsive adjustments for smaller screens */
        @media (max-width: 768px) {
          .transaction-details .nav-tabs .nav-link {
            font-size: 1rem !important;
            padding: 0.5rem 1rem !important;
            min-width: auto;
          }
          
          .transaction-details .nav-tabs .nav-link svg {
            font-size: 1.1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TransactionDetails;
        
